import React, { useState, useEffect, useMemo, useRef } from 'react';
import { initializeApp } from 'firebase/app';
import { 
  getAuth, 
  signInAnonymously, 
  onAuthStateChanged,
  signInWithCustomToken
} from 'firebase/auth';
import { 
  getFirestore, 
  collection, 
  addDoc, 
  updateDoc, 
  doc, 
  getDoc,
  deleteDoc, 
  setDoc, 
  onSnapshot, 
  serverTimestamp, 
  writeBatch
} from 'firebase/firestore';
import { 
  Package, 
  Search, 
  Plus, 
  CheckCircle2, 
  User, 
  Users, 
  ShoppingBag, 
  LogOut, 
  MapPin,
  XCircle,
  Archive,
  Banknote,
  ShieldCheck,
  ChevronRight,
  ChevronDown,
  History,
  AlertCircle,
  HeartHandshake,
  Pencil,
  CalendarCheck,
  KeyRound, 
  Trash2, 
  UserPlus,
  RotateCcw,
  Check,
  PackageMinus,
  ChevronLeft, 
  ArrowUpDown, 
  ListFilter,
  FileDown,
  Printer,
  Truck,
  Clock,
  BarChart3,
  TrendingUp,
  Map,
  Calendar,
  Facebook,
  StickyNote,
  Ban,
  FileText, 
  Filter,
  MessageCircle, 
  Send,
  Lock, 
  Unlock 
} from 'lucide-react';

// --- Firebase Configuration & Initialization ---
const firebaseConfig = {
  apiKey: "AIzaSyBKoFM1zP0Hqj4sPcfFKL5yk4NYIyNbRhw",
  authDomain: "christine-logistics.firebaseapp.com",
  projectId: "christine-logistics",
  storageBucket: "christine-logistics.firebasestorage.app",
  messagingSenderId: "263997768994",
  appId: "1:263997768994:web:f1bc7c7c8e6408de45d17b"
}; 

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);
const appId = typeof __app_id !== 'undefined' ? __app_id : 'default-app-id';

// --- CONSTANTS ---
const PIN_LOGIN_CASHOUT = "100122"; // Level 1 PIN (Now mainly for Cash Out Confirmation)
const PIN_EDIT_DELETE = "2828";     // Level 2 PIN (Higher Security)

const APP_NAME = "28 Boulevard Dropping Area"; 
const FB_PAGE_URL = "https://www.facebook.com/TwentyEightBoulevard";

// ADMIN CREDENTIALS LIST
const ADMIN_USERS = [
  { username: "Realyn", password: "Manager1" },
  { username: "Roselle", password: "Staff1" },
  { username: "Owner", password: "Gem@1" }
];

// LA UNION TOWNS LIST
const LU_TOWNS = [
  "Agoo",
  "Aringay",
  "Bacnotan",
  "Balaoan",
  "Bangar",
  "Bauang",
  "Caba",
  "Damortis - kenjys",
  "Damortis - 5Js",
  "Luna",
  "Naguillian",
  "Pugo",
  "Rosario - elyuheartbox",
  "Rosario - bizznezzline",
  "San Gabriel",
  "San Juan",
  "SFC",
  "Sto Tomas",
  "Tubao",
];

// --- HELPER: SAFE NUMBER PARSING ---
const safeParseFloat = (val) => {
  if (!val) return 0;
  // Convert to string and remove commas and spaces
  const str = String(val).replace(/[, ]/g, '');
  // Matches optional minus sign followed by digits and dots (Allows negative amounts)
  const match = str.match(/-?[0-9.]+/);
  if (!match) return 0;
  const num = parseFloat(match[0]);
  return isNaN(num) ? 0 : num;
};

// --- CUSTOM SEARCHABLE DROPDOWN COMPONENT ---
const SearchableDropdown = ({ value, onChange, options, defaultOption, icon: Icon, placeholder = "Search..." }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState('');
    const wrapperRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const filteredOptions = options.filter(opt =>
        opt.label.toLowerCase().includes(search.toLowerCase())
    );

    const selectedLabel = value === defaultOption?.value 
        ? defaultOption.label 
        : options.find(o => o.value === value)?.label || defaultOption?.label || placeholder;

    return (
        <div ref={wrapperRef} className="relative w-full">
            <button
                type="button"
                onClick={() => { setIsOpen(!isOpen); setSearch(''); }}
                className="w-full flex items-center justify-between px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
                <div className="flex items-center gap-2 overflow-hidden text-slate-700">
                    {Icon && <Icon className="text-slate-400 w-4 h-4 shrink-0" />}
                    <span className="truncate text-sm font-medium">
                        {selectedLabel}
                    </span>
                </div>
                <ChevronDown className="text-slate-400 w-4 h-4 shrink-0 ml-2" />
            </button>

            {isOpen && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-xl overflow-hidden flex flex-col">
                    <div className="p-2 border-b border-slate-100 bg-slate-50">
                        <div className="relative">
                            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400" />
                            <input
                                type="text"
                                autoFocus
                                placeholder={placeholder}
                                className="w-full pl-7 pr-2 py-1.5 text-sm border border-slate-200 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </div>
                    </div>
                    <div className="max-h-60 overflow-y-auto">
                        {defaultOption && (
                            <button
                                type="button"
                                className={`w-full text-left px-3 py-2 text-sm hover:bg-indigo-50 transition-colors ${value === defaultOption.value ? 'bg-indigo-50 font-bold text-indigo-700' : 'text-slate-700'}`}
                                onClick={() => { onChange(defaultOption.value); setIsOpen(false); }}
                            >
                                {defaultOption.label}
                            </button>
                        )}
                        {filteredOptions.map(opt => (
                            <button
                                key={opt.value}
                                type="button"
                                className={`w-full text-left px-3 py-2 text-sm hover:bg-indigo-50 transition-colors ${value === opt.value ? 'bg-indigo-50 font-bold text-indigo-700' : 'text-slate-700'}`}
                                onClick={() => { onChange(opt.value); setIsOpen(false); }}
                            >
                                {opt.label}
                            </button>
                        ))}
                        {filteredOptions.length === 0 && (
                            <div className="px-3 py-4 text-sm text-slate-400 text-center">No results found</div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

// --- Main Application Component ---
export default function App() {
  const [user, setUser] = useState(null);
   
  // Auth State
  const [role, setRole] = useState(null); 
  const [userName, setUserName] = useState('');
   
  // Data State
  const [items, setItems] = useState([]);
  const [sellerList, setSellerList] = useState([]); 
  const [loading, setLoading] = useState(() => !!localStorage.getItem('la_union_role'));
   
  // UI State
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); 
  const [isFormOpen, setIsFormOpen] = useState(false);
   
  // PAGINATION & SORTING STATE
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortBy, setSortBy] = useState('date'); 
  const [sortOrder, setSortOrder] = useState('desc'); 
  const [domFilter, setDomFilter] = useState('all'); 

  // FILTER STATE
  const [sellerFilter, setSellerFilter] = useState('all');

  // MASS ACTION STATE
  const [selectedItems, setSelectedItems] = useState(new Set()); 

  // EDIT MODAL STATE
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [isFinancialsUnlocked, setIsFinancialsUnlocked] = useState(false); 

  const [isCashOutModalOpen, setIsCashOutModalOpen] = useState(false);
  const [isUserMgmtOpen, setIsUserMgmtOpen] = useState(false); 
  const [isProcessing, setIsProcessing] = useState(false);
  const [showSuccessMsg, setShowSuccessMsg] = useState(false); 

  // REPORTS MODAL STATE
  const [isReportsOpen, setIsReportsOpen] = useState(false);
  const [isSellerReportsOpen, setIsSellerReportsOpen] = useState(false);
  const [isPrintingLedger, setIsPrintingLedger] = useState(false);

  // CASH OUT SEARCH & PAGINATION
  const [cashOutSearchTerm, setCashOutSearchTerm] = useState('');
  const [cashOutPage, setCashOutPage] = useState(1);
  const CASH_OUT_ITEMS_PER_PAGE = 10;

  // USER MANAGEMENT SEARCH
  const [userMgmtSearch, setUserMgmtSearch] = useState('');

  // DATE FILTERS STATE
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // --- CHAT STATE ---
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [chatConversations, setChatConversations] = useState([]); 
  const [activeChatId, setActiveChatId] = useState(null); 
  const [chatMessages, setChatMessages] = useState([]); 
  const [chatInput, setChatInput] = useState('');
  const [unreadTotal, setUnreadTotal] = useState(0); 
  const messagesEndRef = useRef(null);

  // Login UI State
  const [loginMode, setLoginMode] = useState('menu'); 
  const [loginInputName, setLoginInputName] = useState('');
  const [loginInputPass, setLoginInputPass] = useState('');
  const [loginError, setLoginError] = useState('');
   
  // Admin Add User State
  const [newSellerName, setNewSellerName] = useState('');
  const [newSellerPass, setNewSellerPass] = useState('');

  // Form State for New Item
  const [newItemItem, setNewItemItem] = useState('');
  const [newItemBuyer, setNewItemBuyer] = useState('');
  const [newItemPrice, setNewItemPrice] = useState('');
  const [newItemTransferFee, setNewItemTransferFee] = useState(''); 
  const [newItemLocation, setNewItemLocation] = useState('Agoo');
  const [newItemSeller, setNewItemSeller] = useState(''); 
  const [newItemStatus, setNewItemStatus] = useState('dropped'); 

  // Cash Out Selection State (For Admin)
  const [selectedSellerForCashout, setSelectedSellerForCashout] = useState(null);

  // --- HELPER: DATE FORMATTER (MM/DD/YY) ---
  const formatDate = (date) => {
    if (!date) return '';
    return date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: '2-digit'
    });
  };

  // --- HELPER: DAYS ON MARKET CALCULATOR ---
  const getDOM = (date) => {
    if (!date) return 0;
    const today = new Date();
    const d1 = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const d2 = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const diffTime = Math.abs(d2 - d1);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)); 
    return diffDays;
  };

  // --- Auth & Data Effects ---

  useEffect(() => {
    const initAuth = async () => {
      try {
        if (typeof __initial_auth_token !== 'undefined' && __initial_auth_token) {
          await signInWithCustomToken(auth, __initial_auth_token);
        } else {
          await signInAnonymously(auth);
        }
      } catch (error) {
        console.error("Auth failed", error);
        setLoading(false);
      }
    };
    initAuth();

    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      const savedRole = localStorage.getItem('la_union_role');
      const savedName = localStorage.getItem('la_union_name');
      if (savedRole && savedName) {
        setRole(savedRole);
        setUserName(savedName);
        if (savedRole === 'buyer') {
          setSearchTerm(savedName);
        }
        
        // AUTO-SET CHAT ID FOR SELLERS
        if (savedRole === 'seller') {
            const safeId = savedName.trim().toLowerCase().replace(/[^a-z0-9_]/g, '_');
            setActiveChatId(safeId);
        }
      } else {
          setLoading(false);
      }
    });
    
    const safetyTimer = setTimeout(() => setLoading(false), 8000);

    return () => {
        unsubscribe();
        clearTimeout(safetyTimer);
    };
  }, []);

  useEffect(() => {
    if (!user) return;
    const itemsRef = collection(db, 'artifacts', appId, 'public', 'data', 'dropping_items');
      
    const unsubscribe = onSnapshot(itemsRef, { includeMetadataChanges: true }, (snapshot) => {
      const loadedItems = snapshot.docs.map(doc => {
        const data = doc.data({ serverTimestamps: 'estimate' }); 
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          claimedAt: data.claimedAt?.toDate() || null,
          cashedOutAt: data.cashedOutAt?.toDate() || null,
          pulledOutAt: data.pulledOutAt?.toDate() || null 
        };
      });
      loadedItems.sort((a, b) => b.createdAt - a.createdAt);
      setItems(loadedItems);
      setLoading(false);
    }, (error) => {
      console.error("Data fetch error:", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [user]);

  useEffect(() => {
    if (role === 'admin') {
       const sellersRef = collection(db, 'artifacts', appId, 'public', 'data', 'sellers');
       const unsubscribe = onSnapshot(sellersRef, (snapshot) => {
         const users = snapshot.docs.map(doc => ({
             id: doc.id,
             ...doc.data()
          }));
          users.sort((a, b) => a.displayName.localeCompare(b.displayName));
          setSellerList(users);
       });
       return () => unsubscribe();
    }
  }, [role]);

  // --- CHAT EFFECTS ---

  // 1. ADMIN: LISTEN TO ALL CONVERSATIONS LIST
  useEffect(() => {
      if (role !== 'admin' || !user) return;

      const chatsRef = collection(db, 'artifacts', appId, 'public', 'data', 'chats');
      const unsubscribe = onSnapshot(chatsRef, (snapshot) => {
          let totalUnread = 0;
          const convos = snapshot.docs.map(doc => {
              const data = doc.data();
              totalUnread += (data.adminUnreadCount || 0);
              return {
                  id: doc.id,
                  ...data,
                  lastMessageTime: data.lastMessageTime?.toDate() || new Date(0)
              };
          });
          convos.sort((a,b) => b.lastMessageTime - a.lastMessageTime);
          setChatConversations(convos);
          setUnreadTotal(totalUnread);
      });
      return () => unsubscribe();
  }, [role, user]);

  // 2. SELLER: LISTEN TO OWN CONVERSATION METADATA (For Unread Badge)
  useEffect(() => {
      if (role !== 'seller' || !activeChatId || !user) return;

      const myChatRef = doc(db, 'artifacts', appId, 'public', 'data', 'chats', activeChatId);
      const unsubscribe = onSnapshot(myChatRef, (docSnap) => {
          if (docSnap.exists()) {
              const data = docSnap.data();
              setUnreadTotal(data.sellerUnreadCount || 0);
          }
      });
      return () => unsubscribe();
  }, [role, activeChatId, user]);

  // 3. MESSAGES: LISTEN TO ACTIVE CHAT MESSAGES
  useEffect(() => {
      if (!isChatOpen || !activeChatId || !user) return;

      const markRead = async () => {
          const chatRef = doc(db, 'artifacts', appId, 'public', 'data', 'chats', activeChatId);
          if (role === 'admin') {
              await updateDoc(chatRef, { adminUnreadCount: 0 });
          } else if (role === 'seller') {
              const snap = await getDoc(chatRef);
              if (snap.exists()) {
                  await updateDoc(chatRef, { sellerUnreadCount: 0 });
              }
          }
      };
      markRead();

      const msgsRef = collection(db, 'artifacts', appId, 'public', 'data', 'chats', activeChatId, 'messages');
      const unsubscribe = onSnapshot(msgsRef, (snapshot) => {
          const loadedMsgs = snapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data(),
              createdAt: doc.data().createdAt?.toDate() || new Date()
          }));
          loadedMsgs.sort((a,b) => a.createdAt - b.createdAt);
          setChatMessages(loadedMsgs);
          
          setTimeout(() => {
              messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
          }, 100);
      });

      return () => unsubscribe();
  }, [isChatOpen, activeChatId, user, role]);


  // --- ADMIN REPORTS CALCULATIONS ---
  const reports = useMemo(() => {
    const defaultReports = {
        totalCashedOutHistory: 0,
        todayPickups: 0,
        todayIncoming: 0,
        todayOutgoing: 0,
        dailyInList: [],
        dailyOutList: [],
        topSellersVolume: [],
        topSellersValue: [],
        topLocations: [],
        busiestDay: { day: '-', count: 0 }
    };

    if (!items || items.length === 0) return defaultReports;

    let totalCashedOutHistory = 0;
    const sellerVolume = {};
    const sellerValue = {};
    const locationCounts = {};
    const dayCounts = {0:0, 1:0, 2:0, 3:0, 4:0, 5:0, 6:0};

    const now = new Date();
    const todayString = now.toDateString(); 
      
    let todayPickups = 0;
    let todayIncoming = 0; 
    let todayOutgoing = 0; 

    const dailyInList = [];
    const dailyOutList = [];

    items.forEach(item => {
        const price = safeParseFloat(item.price);
        const fee = safeParseFloat(item.transferFee);

        if (item.status === 'cashed_out') {
            totalCashedOutHistory += (price + fee);
        }

        const seller = item.sellerName || 'Unknown';
        sellerVolume[seller] = (sellerVolume[seller] || 0) + 1;

        if (item.status === 'claimed' || item.status === 'cashed_out') {
             sellerValue[seller] = (sellerValue[seller] || 0) + price;
        }

        const loc = item.location || 'Unknown';
        locationCounts[loc] = (locationCounts[loc] || 0) + 1;

        const isCollectedStatus = item.status === 'claimed' || item.status === 'cashed_out';

        if (item.claimedAt && isCollectedStatus) {
            const day = item.claimedAt.getDay();
            dayCounts[day]++;

            if (item.claimedAt.toDateString() === todayString) {
                todayPickups++;
                 
                let amountIn = 0;
                if (item.isPaidExternally) {
                    amountIn = fee;
                } else {
                    amountIn = (price + fee);
                }
                todayIncoming += amountIn;

                dailyInList.push({
                    time: item.claimedAt.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}),
                    item: item.itemName,
                    amount: amountIn,
                    type: item.isPaidExternally ? 'Fee Only' : 'Full Payment'
                });
            }
        }

        if (item.cashedOutAt && item.status === 'cashed_out') {
            if (item.cashedOutAt.toDateString() === todayString) {
                 if (!item.isPaidExternally) {
                    todayOutgoing += price;
                    dailyOutList.push({
                        time: item.cashedOutAt.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}),
                        seller: item.sellerName,
                        amount: price,
                        item: item.itemName
                    });
                 }
            }
        }
    });

    const getTop = (obj, limit = 10) => {
        return Object.entries(obj)
            .sort(([,a], [,b]) => b - a)
            .slice(0, limit);
    };

    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    let busiestDayIndex = 0;
    try {
        busiestDayIndex = Object.keys(dayCounts).reduce((a, b) => dayCounts[a] > dayCounts[b] ? a : b);
    } catch (e) { console.log(e); }

    dailyInList.sort((a,b) => a.time.localeCompare(b.time));
    dailyOutList.sort((a,b) => a.time.localeCompare(b.time));

    return {
        totalCashedOutHistory,
        todayPickups,
        todayIncoming,
        todayOutgoing,
        dailyInList,
        dailyOutList,
        topSellersVolume: getTop(sellerVolume),
        topSellersValue: getTop(sellerValue),
        topLocations: getTop(locationCounts),
        busiestDay: { day: days[busiestDayIndex], count: dayCounts[busiestDayIndex] }
    };
  }, [items]);

  // --- NEW: SELLER ANALYTICS CALCULATIONS ---
  const sellerStats = useMemo(() => {
        if (role !== 'seller' || !userName || items.length === 0) return null;
        
        const myItems = items.filter(i => i.sellerName === userName);
        const now = new Date();
        const currentMonth = now.getMonth();
        const currentYear = now.getFullYear();
        const lastMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const lastMonth = lastMonthDate.getMonth();
        const lastMonthYear = lastMonthDate.getFullYear();

        let lifetimeSales = 0;
        let staleItems = 0;
        let thisMonthSales = 0;
        let lastMonthSales = 0;
        const cashOutHistory = [];

        myItems.forEach(item => {
            const price = safeParseFloat(item.price);
            
            if (item.status === 'dropped' && getDOM(item.createdAt) > 30) {
                staleItems++;
            }

            if (item.status === 'claimed' || item.status === 'cashed_out') {
                if (!item.isPaidExternally) {
                    lifetimeSales += price;

                    const actionDate = item.claimedAt || item.cashedOutAt || item.createdAt;
                    if (actionDate) {
                        if (actionDate.getMonth() === currentMonth && actionDate.getFullYear() === currentYear) {
                            thisMonthSales += price;
                        } else if (actionDate.getMonth() === lastMonth && actionDate.getFullYear() === lastMonthYear) {
                            lastMonthSales += price;
                        }
                    }
                }
            }

            if (item.status === 'cashed_out' && item.cashedOutAt && !item.isPaidExternally) {
                cashOutHistory.push({
                    dateObj: item.cashedOutAt,
                    dateKey: item.cashedOutAt.toLocaleDateString(),
                    amount: price
                });
            }
        });

        const groupedHistory = {};
        cashOutHistory.forEach(h => {
            if (!groupedHistory[h.dateKey]) {
                groupedHistory[h.dateKey] = { date: h.dateObj, total: 0, count: 0 };
            }
            groupedHistory[h.dateKey].total += h.amount;
            groupedHistory[h.dateKey].count += 1;
        });

        const recentPayouts = Object.values(groupedHistory)
            .sort((a,b) => b.date - a.date)
            .slice(0, 5); 

        return {
            lifetimeSales,
            staleItems,
            thisMonthSales,
            lastMonthSales,
            recentPayouts
        };
  }, [items, role, userName]);


  // --- Mass Action Handlers ---

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allIds = sortedItems.map(i => i.id); 
      setSelectedItems(new Set(allIds));
    } else {
      setSelectedItems(new Set());
    }
  };

  const handleSelectItem = (id) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedItems(newSelected);
  };

  const handleMassPullOut = async () => {
    if (selectedItems.size === 0) return;
      
    if (!confirm(`Confirm PULL OUT for ${selectedItems.size} items? They will be moved to Archive.`)) return;

    setIsProcessing(true);
    try {
      const batch = writeBatch(db);
      selectedItems.forEach(id => {
        const itemRef = doc(db, 'artifacts', appId, 'public', 'data', 'dropping_items', id);
        batch.update(itemRef, { 
            status: 'pulled_out',
            pulledOutAt: serverTimestamp(),
            lastEditedBy: userName // Tracking User
        });
      });
      await batch.commit();
      
      setSelectedItems(new Set()); 
      alert("Selected items marked as Pulled Out.");
    } catch (error) {
      console.error("Error pulling out items:", error);
      alert("Error: " + error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleMassDelete = async () => {
    if (selectedItems.size === 0) return;
      
    const pin = prompt(`⚠️ DANGER ZONE ⚠️\n\nYou are about to PERMANENTLY DELETE ${selectedItems.size} items.\n\nThis cannot be undone. Please enter the EDIT/DELETE PIN to confirm:`);
      
    if (pin !== PIN_EDIT_DELETE) {
        alert("Incorrect PIN. Deletion cancelled.");
        return;
    }

    setIsProcessing(true);
    try {
      const batch = writeBatch(db);
      selectedItems.forEach(id => {
        const itemRef = doc(db, 'artifacts', appId, 'public', 'data', 'dropping_items', id);
        batch.delete(itemRef);
      });
      await batch.commit();
      
      setSelectedItems(new Set()); 
      alert("Selected items deleted successfully.");
    } catch (error) {
      console.error("Error deleting items:", error);
      alert("Error deleting items: " + error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  // EXPORT TO CSV HANDLER
  const handleExportCSV = () => {
    if (selectedItems.size === 0) return;

    const exportData = items.filter(item => selectedItems.has(item.id));
    const headers = ["Date Added", "Days on Market", "Item Name", "Buyer", "Seller", "Location", "Price", "Transfer Fee", "Status", "Claimed Date", "Paid Externally?", "Remarks", "Last Edited By", "Cashed Out By"];

    const csvRows = [
      headers.join(','), 
      ...exportData.map(item => {
        const escape = (text) => `"${String(text || '').replace(/"/g, '""')}"`;
        return [
          escape(formatDate(item.createdAt)),
          escape(getDOM(item.createdAt)), 
          escape(item.itemName),
          escape(item.buyerName),
          escape(item.sellerName),
          escape(item.location),
          escape(item.price),
          escape(item.transferFee || '0'),
          escape(item.status),
          escape(formatDate(item.claimedAt)),
          escape(item.isPaidExternally ? 'Yes' : 'No'),
          escape(item.remarks || ''),
          escape(item.lastEditedBy || ''),
          escape(item.cashedOutBy || '')
        ].join(',');
      })
    ];

    const csvString = csvRows.join('\n');
    const blob = new Blob([csvString], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `28Blvd_Export_${new Date().toLocaleDateString().replace(/\//g, '-')}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // PRINT RECEIPT HANDLER
  const handlePrintReceipt = () => {
    window.print();
  };

  const handlePrintLedger = () => {
      setIsPrintingLedger(true);
      setTimeout(() => {
          window.print();
          setIsPrintingLedger(false);
      }, 500);
  };

  // --- Login Handlers ---

  const handleAdminLogin = () => {
    const adminUser = ADMIN_USERS.find(
        u => u.username.toLowerCase() === loginInputName.trim().toLowerCase() && u.password === loginInputPass
    );

    if (adminUser) {
      completeLogin('admin', adminUser.username); 
    } else {
      setLoginError('Invalid Username or Password');
    }
  };

  const handleSellerLogin = async () => {
    if (!loginInputName.trim() || !loginInputPass.trim()) {
      setLoginError('Please enter name and password');
      return;
    }

    const safeId = loginInputName.trim().toLowerCase().replace(/[^a-z0-9_]/g, '_');
    const userRef = doc(db, 'artifacts', appId, 'public', 'data', 'sellers', safeId);
      
    try {
      const userSnap = await getDoc(userRef);
       
      if (userSnap.exists()) {
        const userData = userSnap.data();
        if (userData.password === loginInputPass) {
          completeLogin('seller', userData.displayName);
        } else {
          setLoginError('Incorrect password');
        }
      } else {
        setLoginError('Account not found. Please ask Admin to create your account.');
      }
    } catch (err) {
      console.error("Auth Error:", err);
      setLoginError('Error: ' + err.message);
    }
  };

  const handleBuyerLogin = () => {
    if (!loginInputName.trim()) return;
    completeLogin('buyer', loginInputName);
  };

  const completeLogin = (newRole, newName) => {
    setRole(newRole);
    setUserName(newName);
    localStorage.setItem('la_union_role', newRole);
    localStorage.setItem('la_union_name', newName);
    if (newRole === 'buyer') {
      setSearchTerm(newName);
    }
    
    if (newRole === 'seller') {
        const safeId = newName.trim().toLowerCase().replace(/[^a-z0-9_]/g, '_');
        setActiveChatId(safeId);
    }

    setLoginMode('menu');
    setLoginInputName('');
    setLoginInputPass('');
    setLoginError('');
  };

  const handleLogout = () => {
    setRole(null);
    setUserName('');
    localStorage.removeItem('la_union_role');
    localStorage.removeItem('la_union_name');
    setLoginMode('menu');
    setSelectedSellerForCashout(null);
    setIsUserMgmtOpen(false);
    setSelectedItems(new Set()); 
    setSearchTerm(''); 
    setCashOutSearchTerm('');
    setCashOutPage(1);
    setUserMgmtSearch(''); 
    setDomFilter('all'); 
    setStartDate(''); 
    setEndDate('');
    setSellerFilter('all'); 
    
    setIsChatOpen(false);
    setActiveChatId(null);
    setChatMessages([]);
    setUnreadTotal(0);
  };

  // --- Data Handlers ---

  const handleAddItem = async (e) => {
    e.preventDefault();
    if (!newItemItem || !newItemBuyer) return;

    const actualSeller = role === 'admin' ? newItemSeller : userName;

    if (!actualSeller) {
        alert("Please select a Seller.");
        return;
    }

    // High Value Check (Prevents accidental extra zeros, now handles negatives)
    const priceVal = safeParseFloat(newItemPrice);
    if (Math.abs(priceVal) >= 1000) {
        if (!window.confirm(`⚠️ HIGH AMOUNT WARNING ⚠️\n\nYou entered a price of ₱${priceVal.toLocaleString()}.\n\nAre you sure this is correct?`)) {
            return;
        }
    }

    try {
      const itemsRef = collection(db, 'artifacts', appId, 'public', 'data', 'dropping_items');
      await addDoc(itemsRef, {
        itemName: newItemItem,
        buyerName: newItemBuyer,
        sellerName: actualSeller, 
        location: newItemLocation,
        price: newItemPrice || '0',
        transferFee: newItemTransferFee || '0', 
        status: newItemStatus || 'dropped', 
        isPaidExternally: false, 
        createdAt: serverTimestamp(),
        lastEditedBy: role === 'admin' ? userName : 'Seller' 
      });
      
      setNewItemItem('');
      setNewItemBuyer('');
      setNewItemPrice('');
      setNewItemTransferFee(''); 
      // Seller reset removed from here so it persists between rapid entries!
      
      setShowSuccessMsg(true);
      setTimeout(() => setShowSuccessMsg(false), 2000);

    } catch (error) {
      console.error("Error adding item:", error);
      alert("Error adding item: " + error.message);
    }
  };

  const handleStatusChange = async (itemId, newStatus) => {
    try {
      const itemRef = doc(db, 'artifacts', appId, 'public', 'data', 'dropping_items', itemId);
      const updates = { 
          status: newStatus,
          lastEditedBy: userName 
      };
      
      if (newStatus === 'claimed') {
        updates.claimedAt = serverTimestamp();
      } else if (newStatus === 'dropped' || newStatus === 'in_transit') {
        updates.claimedAt = null; 
        updates.cashedOutAt = null; 
        updates.pulledOutAt = null; 
      } else if (newStatus === 'pulled_out') {
          updates.pulledOutAt = serverTimestamp(); 
      }

      await updateDoc(itemRef, updates);
    } catch (error) {
      console.error("Error updating status:", error);
      alert("Error updating status: " + error.message);
    }
  };

  // --- User Management Handlers (Admin) ---

  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (!newSellerName || !newSellerPass) return;

    const safeId = newSellerName.trim().toLowerCase().replace(/[^a-z0-9_]/g, '_');
    const userRef = doc(db, 'artifacts', appId, 'public', 'data', 'sellers', safeId);

    try {
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
            alert("Error: A seller with this name (or similar) already exists.");
            return;
        }

        await setDoc(userRef, {
            displayName: newSellerName,
            password: newSellerPass,
            role: 'seller',
            createdAt: serverTimestamp()
        });

        alert(`Success! Account created for ${newSellerName}`);
        setNewSellerName('');
        setNewSellerPass('');
    } catch (error) {
        alert("Error creating user: " + error.message);
    }
  };

  const handleResetPassword = async (sellerId, sellerName) => {
    const newPass = prompt(`Enter new password for ${sellerName}:`);
    if (!newPass) return;
      
    try {
      await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'sellers', sellerId), {
        password: newPass
      });
      alert("Password updated successfully.");
    } catch (error) {
      alert("Error updating password: " + error.message);
    }
  };

  const handleDeleteUser = async (sellerId, sellerName) => {
    const pin = prompt(`⚠️ WARNING ⚠️\n\nYou are about to remove access for ${sellerName}.\n\nPlease enter the EDIT/DELETE PIN to confirm:`);
      
    if (pin !== PIN_EDIT_DELETE) {
        alert("Incorrect PIN. Action cancelled.");
        return;
    }
      
    try {
      await deleteDoc(doc(db, 'artifacts', appId, 'public', 'data', 'sellers', sellerId));
      alert(`User ${sellerName} has been removed.`);
    } catch (error) {
      alert("Error removing user: " + error.message);
    }
  };

  // --- Chat Handlers ---
   
  const handleSendMessage = async (e) => {
      e.preventDefault();
      if (!chatInput.trim() || !activeChatId) return;

      const text = chatInput.trim();
      setChatInput('');

      const chatDocRef = doc(db, 'artifacts', appId, 'public', 'data', 'chats', activeChatId);
      const messagesRef = collection(db, 'artifacts', appId, 'public', 'data', 'chats', activeChatId, 'messages');

      try {
          await addDoc(messagesRef, {
              text: text,
              sender: role, 
              createdAt: serverTimestamp()
          });

          const chatDocSnap = await getDoc(chatDocRef);
          
          const updates = {
              lastMessage: text,
              lastMessageTime: serverTimestamp(),
              sellerName: role === 'seller' ? userName : (chatDocSnap.exists() ? chatDocSnap.data().sellerName : activeChatId)
          };

          if (role === 'admin') {
              const current = chatDocSnap.exists() ? (chatDocSnap.data().sellerUnreadCount || 0) : 0;
              updates.sellerUnreadCount = current + 1;
          } else {
              const current = chatDocSnap.exists() ? (chatDocSnap.data().adminUnreadCount || 0) : 0;
              updates.adminUnreadCount = current + 1;
          }

          if (!chatDocSnap.exists()) {
              await setDoc(chatDocRef, {
                  ...updates,
                  createdAt: serverTimestamp()
              });
          } else {
              await updateDoc(chatDocRef, updates);
          }

      } catch (err) {
          console.error("Send error", err);
          alert("Failed to send message.");
      }
  };

  // --- User Management Filtering ---
  const filteredSellerList = useMemo(() => {
    if (!userMgmtSearch.trim()) return []; 
    const term = userMgmtSearch.toLowerCase();
    return sellerList.filter(s => s.displayName.toLowerCase().includes(term));
  }, [sellerList, userMgmtSearch]);

  // --- Edit Logic (For Admin) ---

  const handleEditClick = (item) => {
    setEditingItem({ transferFee: '0', remarks: '', ...item }); 
    setIsFinancialsUnlocked(false); 
    setIsEditModalOpen(true);
  };

  const handleUnlockFinancials = (e) => {
      e.preventDefault();
      const pin = prompt("Enter PIN to unlock Amount/Fee editing:");
      if (pin === PIN_EDIT_DELETE) {
          setIsFinancialsUnlocked(true);
      } else {
          alert("Incorrect PIN.");
      }
  };

  const handleUpdateItem = async (e) => {
    e.preventDefault();
    if (!editingItem) return;

    try {
      const itemRef = doc(db, 'artifacts', appId, 'public', 'data', 'dropping_items', editingItem.id);
      
      const updates = {
        itemName: editingItem.itemName,
        buyerName: editingItem.buyerName,
        price: editingItem.price,
        transferFee: editingItem.transferFee,
        location: editingItem.location,
        isPaidExternally: editingItem.isPaidExternally || false,
        status: editingItem.status,
        remarks: editingItem.remarks || '',
        lastEditedBy: userName // TRACKING
      };

      const original = items.find(i => i.id === editingItem.id);
      if (editingItem.status === 'claimed' && original?.status !== 'claimed') {
          updates.claimedAt = serverTimestamp();
      }

      if (editingItem.status === 'cashed_out' && original?.status !== 'cashed_out') {
          updates.cashedOutAt = serverTimestamp();
          updates.cashedOutBy = userName; // TRACKING
          if (!original?.claimedAt) {
             updates.claimedAt = serverTimestamp();
          }
      }
      
      if (editingItem.status === 'dropped' || editingItem.status === 'in_transit') {
          updates.claimedAt = null;
          updates.cashedOutAt = null;
          updates.pulledOutAt = null;
          updates.cashedOutBy = null;
      }

      await updateDoc(itemRef, updates);
      
      setIsEditModalOpen(false);
      setEditingItem(null);
    } catch (error) {
      alert("Error updating item: " + error.message);
    }
  };

  // --- Admin Cash Out Logic ---

  const sellersWithBalance = useMemo(() => {
    if (role !== 'admin') return [];
      
    const groups = {};
    items.forEach(item => {
      if (item.status === 'claimed') {
        const seller = item.sellerName || 'Unknown';
        if (!groups[seller]) {
          groups[seller] = { 
            name: seller, 
            items: [], 
            total: 0,
            feesTotal: 0, 
            itemsTotal: 0 
          };
        }
        groups[seller].items.push(item);
        
        const price = safeParseFloat(item.price);
        const fee = safeParseFloat(item.transferFee);
        
        if (item.isPaidExternally) {
            groups[seller].total += fee;
            groups[seller].feesTotal += fee;
        } else {
            groups[seller].total += (price + fee);
            groups[seller].feesTotal += fee;
            groups[seller].itemsTotal += price;
        }
      }
    });
    return Object.values(groups).sort((a, b) => a.name.localeCompare(b.name));
  }, [items, role]);

  // Filter & Paginate Cash Out List
  const filteredCashOutSellers = useMemo(() => {
    let result = sellersWithBalance;
    if (cashOutSearchTerm.trim()) {
      const lowerTerm = cashOutSearchTerm.toLowerCase();
      result = result.filter(seller => seller.name.toLowerCase().includes(lowerTerm));
    }
    return result;
  }, [sellersWithBalance, cashOutSearchTerm]);

  const paginatedCashOutSellers = useMemo(() => {
    const startIndex = (cashOutPage - 1) * CASH_OUT_ITEMS_PER_PAGE;
    return filteredCashOutSellers.slice(startIndex, startIndex + CASH_OUT_ITEMS_PER_PAGE);
  }, [filteredCashOutSellers, cashOutPage]);

  const totalCashOutPages = Math.ceil(filteredCashOutSellers.length / CASH_OUT_ITEMS_PER_PAGE);

  useEffect(() => {
    if (isCashOutModalOpen) {
        setCashOutPage(1);
    }
  }, [cashOutSearchTerm, isCashOutModalOpen]);

  const confirmCashOutForSeller = async () => {
    if (!selectedSellerForCashout) return;
    if (isProcessing) return;
      
    const pin = prompt(`Confirm Cash Out for ${selectedSellerForCashout.name}.\n\nEnter CONFIRMATION PIN to proceed:`);
    if (pin !== PIN_LOGIN_CASHOUT) {
        alert("Incorrect PIN. Cash Out cancelled.");
        return;
    }

    const itemsToProcess = selectedSellerForCashout.items.map(i => i.id);
    const sellerName = selectedSellerForCashout.name;

    setIsProcessing(true);

    try {
      const batchSize = 250; 
      
      for (let i = 0; i < itemsToProcess.length; i += batchSize) {
        const chunk = itemsToProcess.slice(i, i + batchSize);
        const batch = writeBatch(db);
        
        chunk.forEach(id => {
            const itemRef = doc(db, 'artifacts', appId, 'public', 'data', 'dropping_items', id);
            batch.update(itemRef, { 
                status: 'cashed_out',
                cashedOutAt: serverTimestamp(),
                cashedOutBy: userName 
            });
        });

        await batch.commit();
      }
      
      setTimeout(() => {
          alert(`Success! Payout recorded for ${sellerName}. Processed by ${userName}.`);
          setSelectedSellerForCashout(null);
          setIsProcessing(false);
      }, 500);

    } catch (error) {
      console.error("Cash out error", error);
      alert("System Error: " + error.message);
      setIsProcessing(false);
    }
  };

  // --- FILTERING, SORTING & PAGINATION LOGIC ---

  const filteredItems = useMemo(() => {
    if (role === 'buyer' && !searchTerm.trim()) {
        return [];
    }

    return items.filter(item => {
      if (role === 'seller' && item.sellerName !== userName) {
        return false;
      }

      const searchLower = searchTerm.toLowerCase();
      let matchesSearch = false;
        
      if (role === 'buyer') {
          matchesSearch = 
            item.buyerName.toLowerCase().includes(searchLower) || 
            (item.location && item.location.toLowerCase().includes(searchLower));
      } else {
          matchesSearch = 
            item.itemName.toLowerCase().includes(searchLower) ||
            item.buyerName.toLowerCase().includes(searchLower) ||
            item.sellerName.toLowerCase().includes(searchLower) ||
            (item.location && item.location.toLowerCase().includes(searchLower)) ||
            (item.price && item.price.toString().includes(searchLower)); 
      }

      if (role === 'admin' && sellerFilter !== 'all' && item.sellerName !== sellerFilter) {
          return false;
      }

      let matchesStatus = true;
      if (statusFilter === 'all') {
         if (item.status === 'cashed_out' || item.status === 'pulled_out' || item.status === 'disposed') {
             matchesStatus = false;
         }
      } else if (statusFilter === 'cashed_out') {
         matchesStatus = item.status === 'cashed_out' || item.status === 'pulled_out' || item.status === 'disposed';
      } else {
         matchesStatus = item.status === statusFilter;
      }

      let matchesDOM = true;
      if (domFilter !== 'all') {
          const days = parseInt(domFilter);
          const itemAge = getDOM(item.createdAt);
          if (itemAge < days) {
              matchesDOM = false;
          }
      }

      let matchesDate = true;
      if (startDate || endDate) {
          let dateToCheck = item.createdAt;

          if (statusFilter === 'claimed' && item.claimedAt) {
              dateToCheck = item.claimedAt;
          } else if (statusFilter === 'cashed_out' && (item.cashedOutAt || item.pulledOutAt)) {
              if (item.status === 'cashed_out' && item.cashedOutAt) dateToCheck = item.cashedOutAt;
              if (item.status === 'pulled_out' && item.pulledOutAt) dateToCheck = item.pulledOutAt;
          }

          const itemDate = new Date(dateToCheck);
          itemDate.setHours(0, 0, 0, 0);

          if (startDate) {
              const start = new Date(startDate);
              start.setHours(0, 0, 0, 0);
              if (itemDate < start) matchesDate = false;
          }

          if (endDate) {
              const end = new Date(endDate);
              end.setHours(23, 59, 59, 999); 
              if (itemDate > end) matchesDate = false;
          }
      }

      return matchesSearch && matchesStatus && matchesDOM && matchesDate;
    });
  }, [items, searchTerm, statusFilter, role, userName, domFilter, startDate, endDate, sellerFilter]); 

  const sortedItems = useMemo(() => {
    const sortable = [...filteredItems];
      
    sortable.sort((a, b) => {
      let valA, valB;
       
      switch(sortBy) {
        case 'name':
          valA = a.itemName.toLowerCase();
          valB = b.itemName.toLowerCase();
          break;
        case 'location':
          valA = (a.location || '').toLowerCase();
          valB = (b.location || '').toLowerCase();
          break;
        default: 
          valA = a.createdAt;
          valB = b.createdAt;
      }

      if (valA < valB) return sortOrder === 'asc' ? -1 : 1;
      if (valA > valB) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return sortable;
  }, [filteredItems, sortBy, sortOrder]);

  const paginatedItems = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return sortedItems.slice(startIndex, startIndex + itemsPerPage);
  }, [sortedItems, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(sortedItems.length / itemsPerPage);

  const showingStart = sortedItems.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0;
  const showingEnd = Math.min(currentPage * itemsPerPage, sortedItems.length);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, sortBy, sortOrder, itemsPerPage, domFilter, sellerFilter]);

  const stats = useMemo(() => {
    let viewableItems = items;
    if (role === 'seller') {
        viewableItems = items.filter(i => i.sellerName === userName);
    } else if (role === 'buyer') {
        viewableItems = []; 
    }
      
    let availableBalance = 0;
    if (role === 'seller') {
        availableBalance = viewableItems.reduce((sum, item) => {
            if (item.status === 'claimed') {
                 const price = safeParseFloat(item.price);
                 const fee = safeParseFloat(item.transferFee);
                 
                 if (item.isPaidExternally) {
                    return sum + fee;
                 } else {
                    return sum + price + fee;
                 }
            }
            return sum;
        }, 0);
    }
      
    return {
      total: viewableItems.length,
      dropped: viewableItems.filter(i => i.status === 'dropped').length,
      claimed: viewableItems.filter(i => i.status === 'claimed').length,
      cashed_out: viewableItems.filter(i => i.status === 'cashed_out' || i.status === 'pulled_out').length,
      in_transit: viewableItems.filter(i => i.status === 'in_transit').length, 
      balance: availableBalance 
    };
  }, [items, role, userName]);


  // --- RENDER ---

  if (loading) {
    return (
      <div className="min-h-screen bg-teal-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  // --- PRINT LEDGER VIEW (Hidden unless isPrintingLedger) ---
  if (isPrintingLedger && reports) {
      return (
          <div className="p-8 bg-white text-slate-900 font-mono text-sm">
              <div className="text-center mb-6">
                  <h1 className="text-2xl font-bold">{APP_NAME}</h1>
                  <p>Daily Balancing Sheet (Z-Read)</p>
                  <p className="text-xs mt-1">{new Date().toLocaleDateString()}</p>
              </div>

              {/* SUMMARY BOX */}
              <div className="border border-slate-800 p-4 mb-6 grid grid-cols-3 gap-4 text-center">
                  <div>
                      <div className="font-bold">TOTAL IN</div>
                      <div className="text-lg">₱{reports.todayIncoming.toLocaleString()}</div>
                  </div>
                  <div>
                      <div className="font-bold">TOTAL OUT</div>
                      <div className="text-lg">₱{reports.todayOutgoing.toLocaleString()}</div>
                  </div>
                  <div>
                      <div className="font-bold">NET CASH</div>
                      <div className="text-lg border-t-2 border-slate-800 mt-1 pt-1">₱{(reports.todayIncoming - reports.todayOutgoing).toLocaleString()}</div>
                  </div>
              </div>

              {/* LISTS */}
              <div className="grid grid-cols-2 gap-8">
                  <div>
                      <h3 className="font-bold border-b border-slate-800 mb-2">CASH IN (Claims)</h3>
                      {reports.dailyInList.length === 0 ? <p className="italic text-slate-500">No collections.</p> : (
                          <table className="w-full text-xs">
                              <tbody>
                                  {reports.dailyInList.map((x, i) => (
                                      <tr key={i}>
                                          <td className="pr-2">{x.time}</td>
                                          <td className="truncate max-w-[100px]">{x.item}</td>
                                          <td className="text-right">₱{x.amount}</td>
                                      </tr>
                                  ))}
                              </tbody>
                          </table>
                      )}
                  </div>
                  <div>
                      <h3 className="font-bold border-b border-slate-800 mb-2">CASH OUT (Payouts)</h3>
                      {reports.dailyOutList.length === 0 ? <p className="italic text-slate-500">No payouts.</p> : (
                          <table className="w-full text-xs">
                              <tbody>
                                  {reports.dailyOutList.map((x, i) => (
                                      <tr key={i}>
                                          <td className="pr-2">{x.time}</td>
                                          <td className="truncate max-w-[100px]">{x.seller}</td>
                                          <td className="text-right">₱{x.amount}</td>
                                      </tr>
                                  ))}
                              </tbody>
                          </table>
                      )}
                  </div>
              </div>
              
              <div className="mt-12 pt-8 border-t border-slate-300 text-center text-xs">
                  Prepared by: _______________________ &nbsp;&nbsp;&nbsp; Verified by: _______________________
              </div>
          </div>
      );
  }

  // --- LOGIN SCREENS ---
  if (!role) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-500 to-slate-600 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full relative overflow-hidden">
          <div className="text-center mb-8">
            <div className="mx-auto mb-6 flex justify-center">
              <img src="/logo.png" alt="28Blvd Logo" className="h-24 w-auto object-contain" />
            </div>
            <h1 className="text-2xl font-bold text-slate-800">{APP_NAME}</h1>
            <p className="text-indigo-500 font-medium">Track your item anytime, anywhere</p>
            <a href={FB_PAGE_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline mt-2">
                <Facebook className="w-3 h-3" /> 28 Boulevard Dropping and Fashion Boutique
            </a>
          </div>

          {loginMode === 'menu' && (
            <div className="space-y-3">
              <button onClick={() => { setLoginMode('seller'); }} className="w-full flex items-center p-4 bg-white border-2 border-indigo-100 rounded-xl hover:border-indigo-500 hover:bg-indigo-50 transition-all group">
                <div className="bg-indigo-100 p-2 rounded-lg mr-4 group-hover:bg-indigo-200"><ShoppingBag className="text-indigo-600 w-6 h-6" /></div>
                <div className="text-left"><div className="font-bold text-slate-700">Seller Login</div><div className="text-xs text-slate-400">Drop items, track sales</div></div>
                <ChevronRight className="ml-auto text-slate-300 group-hover:text-indigo-500" />
              </button>
              <button onClick={() => setLoginMode('buyer')} className="w-full flex items-center p-4 bg-white border-2 border-slate-100 rounded-xl hover:border-slate-500 hover:bg-slate-50 transition-all group">
                <div className="bg-slate-100 p-2 rounded-lg mr-4 group-hover:bg-slate-200"><User className="text-slate-600 w-6 h-6" /></div>
                <div className="text-left"><div className="font-bold text-slate-700">Parcel Tracker</div><div className="text-xs text-slate-400">Check for your packages</div></div>
                <ChevronRight className="ml-auto text-slate-300 group-hover:text-slate-500" />
              </button>
              <button onClick={() => setLoginMode('admin')} className="w-full flex items-center p-4 bg-white border-2 border-slate-100 rounded-xl hover:border-slate-800 hover:bg-slate-50 transition-all group">
                <div className="bg-slate-100 p-2 rounded-lg mr-4 group-hover:bg-slate-200"><ShieldCheck className="text-slate-600 w-6 h-6" /></div>
                <div className="text-left"><div className="font-bold text-slate-700">Admin</div><div className="text-xs text-slate-400">Manage center</div></div>
              </button>
            </div>
          )}

          {loginMode === 'admin' && (
            <div className="animate-in slide-in-from-right fade-in duration-300">
              <h2 className="text-lg font-bold text-slate-800 mb-4">Admin Access</h2>
              <div className="space-y-3">
                  <input type="text" placeholder="Admin Username" value={loginInputName} onChange={(e) => setLoginInputName(e.target.value)} className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-500 outline-none" />
                  <input type="password" placeholder="Password" value={loginInputPass} onChange={(e) => setLoginInputPass(e.target.value)} className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-500 outline-none" />
              </div>
              {loginError && <p className="text-red-500 text-sm mt-3 text-center">{loginError}</p>}
              <button onClick={handleAdminLogin} className="w-full bg-slate-800 text-white py-3 rounded-lg font-semibold hover:bg-slate-900 mt-4 mb-3">Login</button>
              <button onClick={() => { setLoginMode('menu'); setLoginError(''); setLoginInputName(''); setLoginInputPass(''); }} className="w-full text-slate-500 text-sm hover:underline">Back</button>
            </div>
          )}

          {loginMode === 'seller' && (
            <div className="animate-in slide-in-from-right fade-in duration-300">
              <h2 className="text-lg font-bold text-slate-800 mb-2">Seller Login</h2>
              <div className="space-y-3">
                <input type="text" placeholder="Shop Name / Username" value={loginInputName} onChange={(e) => setLoginInputName(e.target.value)} className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none" />
                <input type="password" placeholder="Password" value={loginInputPass} onChange={(e) => setLoginInputPass(e.target.value)} className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none" />
              </div>
              {loginError && <p className="text-red-500 text-sm mt-3 text-center">{loginError}</p>}
              <button onClick={handleSellerLogin} className="w-full bg-indigo-600 text-white py-3 rounded-lg font-semibold hover:bg-indigo-700 mt-4 mb-2 shadow-md shadow-indigo-200">Login</button>
              <div className="text-center mb-4"><p className="text-xs text-slate-400">Don't have an account? Please contact Admin.</p></div>
              <button onClick={() => { setLoginMode('menu'); setLoginError(''); }} className="w-full text-slate-500 text-sm hover:underline">Back to Menu</button>
            </div>
          )}

          {loginMode === 'buyer' && (
            <div className="animate-in slide-in-from-right fade-in duration-300">
              <h2 className="text-lg font-bold text-slate-800 mb-4">Buyer Access</h2>
              <input type="text" placeholder="Enter your Name or Town" value={loginInputName} onChange={(e) => setLoginInputName(e.target.value)} className="w-full px-4 py-3 border border-slate-300 rounded-lg mb-4 focus:ring-2 focus:ring-slate-500 outline-none" />
              <button onClick={handleBuyerLogin} className="w-full bg-slate-600 text-white py-3 rounded-lg font-semibold hover:bg-slate-700 mb-3">Check My Items</button>
              <button onClick={() => setLoginMode('menu')} className="w-full text-slate-500 text-sm hover:underline">Back</button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // --- MAIN APP RENDER ---
  return (
    <div className="min-h-screen bg-teal-50 pb-20">
       
      {/* STYLES FOR PRINTING */}
      <style>{`
        @media print {
          header, main, .fixed {
            display: none !important;
          }
          html, body, #root, .min-h-screen {
            height: auto !important;
            overflow: visible !important;
            background: white !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          #printable-receipt {
            display: block !important;
            position: static !important;
            width: 100% !important;
            margin: 0 !important;
            padding: 20px !important;
            visibility: visible !important;
          }
          #printable-receipt * {
            visibility: visible !important;
          }
          tr {
              break-inside: avoid; 
              page-break-inside: avoid;
          }
          thead {
            display: table-header-group;
          }
          @page {
            size: auto;
            margin: 10mm;
          }
        }
      `}</style>

      {/* Navbar */}
      <header className="bg-white shadow-sm sticky top-0 z-10 print:hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-2">
              <img src="/logo.png" alt="Logo" className="w-8 h-8 object-contain" />
              <div>
                <h1 className="text-xl font-bold text-slate-800 hidden sm:block">{APP_NAME}</h1>
                <h1 className="text-xl font-bold text-slate-800 sm:hidden">28Blvd</h1>
                <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${role === 'admin' ? 'bg-slate-800 text-white' : role === 'seller' ? 'bg-indigo-100 text-indigo-800' : 'bg-slate-100 text-slate-800'}`}>{role}</span>
                    <span className="text-xs text-slate-500">{userName}</span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
                {/* SELLER HEADER BUTTONS */}
                {role === 'seller' && (
                    <button 
                        onClick={() => setIsSellerReportsOpen(true)}
                        className="p-2 rounded-lg text-slate-600 hover:text-indigo-700 hover:bg-indigo-50 transition-colors"
                        title="My Business"
                    >
                        <BarChart3 className="w-5 h-5" />
                    </button>
                )}

                {/* ADMIN HEADER BUTTONS */}
                {role === 'admin' && (
                    <>
                        <button 
                            onClick={() => setIsFormOpen(true)}
                            className="flex items-center gap-1 bg-indigo-600 text-white px-3 py-2 rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors shadow-sm"
                        >
                            <Plus className="w-4 h-4" />
                            <span className="hidden sm:inline">New Drop</span>
                        </button>

                        <button 
                            onClick={() => setIsCashOutModalOpen(true)}
                            className="flex items-center gap-1 bg-slate-800 text-white px-3 py-2 rounded-lg text-sm font-medium hover:bg-slate-900 transition-colors shadow-sm"
                        >
                            <Banknote className="w-4 h-4" />
                            <span className="hidden sm:inline">Cash Out</span>
                        </button>
                        
                        <button 
                            onClick={() => setIsReportsOpen(true)}
                            className="p-2 rounded-lg text-slate-600 hover:text-slate-700 hover:bg-slate-50 transition-colors"
                            title="Business Reports"
                        >
                            <BarChart3 className="w-5 h-5" />
                        </button>

                        <button 
                            onClick={() => setIsUserMgmtOpen(true)}
                            className="p-2 rounded-lg text-slate-600 hover:text-slate-700 hover:bg-slate-50 transition-colors"
                            title="Manage Users"
                        >
                            <Users className="w-5 h-5" />
                        </button>
                    </>
                )}

                <button 
                onClick={handleLogout}
                className="text-slate-400 hover:text-indigo-600 transition-colors p-2"
                title="Logout"
                >
                <LogOut className="w-5 h-5" />
                </button>
            </div>
          </div>
        </div>
      </header>

      {/* PRINT ONLY INVOICE (Hidden in normal view) */}
      <div id="printable-receipt" className="hidden print:block p-8">
        {selectedSellerForCashout && (
            <div>
                <div className="text-center mb-8">
                    <h1 className="text-2xl font-bold text-slate-800">{APP_NAME}</h1>
                    <p className="text-sm text-indigo-600">Cash Out Receipt / Invoice</p>
                    <p className="text-xs text-slate-500 mt-1">Date: {new Date().toLocaleDateString()}</p>
                    <p className="text-[10px] text-slate-400 mt-1">Processed by: {userName}</p>
                </div>

                <div className="bg-teal-50 p-4 rounded-lg border border-indigo-100 mb-6">
                    <div className="flex justify-between mb-2">
                        <span className="text-slate-500">Seller Name:</span>
                        <span className="font-bold text-lg text-slate-800">{selectedSellerForCashout.name}</span>
                    </div>
                    <div className="flex justify-between">
                            <span className="text-slate-500">Transaction ID:</span>
                            <span className="font-mono text-xs text-slate-600">{selectedSellerForCashout.items[0]?.id.substring(0,8).toUpperCase()}...</span>
                    </div>
                </div>

                <table className="w-full mb-6 text-sm">
                    <thead>
                        <tr className="border-b-2 border-indigo-100 text-left">
                            <th className="py-2 text-slate-500">Date Claimed</th>
                            <th className="py-2 text-slate-500">Item</th>
                            <th className="py-2 text-slate-500">Location</th>
                            <th className="py-2 text-slate-500">Buyer</th>
                            <th className="py-2 text-slate-500 text-right">Fee</th>
                            <th className="py-2 text-slate-500 text-right">Price</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-indigo-50">
                        {selectedSellerForCashout.items.map(item => (
                            <tr key={item.id} className="break-inside-avoid">
                                <td className="py-2 text-slate-500 text-xs">{formatDate(item.claimedAt || item.createdAt)}</td>
                                <td className="py-2 text-slate-700">{item.itemName}</td>
                                <td className="py-2 text-slate-500 text-xs">{item.location}</td>
                                <td className="py-2 text-slate-500">{item.buyerName}</td>
                                <td className="py-2 text-right text-indigo-500 text-xs">{item.transferFee}</td>
                                <td className="py-2 text-right font-medium text-slate-700">
                                    {item.isPaidExternally ? <span className="text-gray-400 line-through decoration-double">({item.price})</span> : `₱${item.price}`}
                                    {item.cashedOutAt && <div className="text-[9px] text-slate-400">{formatDate(item.cashedOutAt)}</div>}
                                </td>
                            </tr>
                        ))}
                        <tr className="border-t-2 border-slate-800 break-inside-avoid bg-slate-50 print:bg-transparent">
                            <td colSpan="5" className="py-4 font-bold text-slate-800 text-right pr-4">TOTAL PAYOUT:</td>
                            <td className="py-4 font-bold text-xl text-slate-600 text-right">₱{selectedSellerForCashout.total.toLocaleString()}</td>
                        </tr>
                    </tbody>
                    <tfoot>
                        <tr className="border-t-2 border-slate-300">
                            <td colSpan="5" className="py-2 text-slate-500 text-right pr-4">Total Item Prices:</td>
                            <td className="py-2 text-slate-700 text-right font-medium">₱{selectedSellerForCashout.itemsTotal.toLocaleString()}</td>
                        </tr>
                        <tr>
                            <td colSpan="5" className="py-2 text-slate-500 text-right pr-4">Total Fees:</td>
                            <td className="py-2 text-slate-700 text-right font-medium">₱{selectedSellerForCashout.feesTotal.toLocaleString()}</td>
                        </tr>
                        <tr className="border-t border-slate-800 break-inside-avoid bg-slate-50 print:bg-transparent">
                            <td colSpan="5" className="py-4 font-bold text-slate-800 text-right pr-4">GRAND TOTAL PAYOUT:</td>
                            <td className="py-4 font-bold text-xl text-slate-800 text-right">₱{selectedSellerForCashout.total.toLocaleString()}</td>
                        </tr>
                    </tfoot>
                </table>

                <div className="mt-12 pt-8 border-t border-slate-300 hidden print:block">
                    <div className="flex justify-between text-xs text-slate-500">
                        <div className="text-center w-1/3"><div className="h-8 border-b border-slate-300 mb-1"></div>Seller Signature</div>
                        <div className="text-center w-1/3"><div className="h-8 border-b border-slate-300 mb-1"></div>Admin Signature: {userName}</div>
                    </div>
                </div>

                <div className="flex gap-3 justify-end mt-6 border-t border-indigo-100 pt-6 print:hidden">
                         <button onClick={() => setSelectedSellerForCashout(null)} className="px-4 py-2 text-slate-500 hover:text-indigo-600">Back</button>
                         <button 
                            onClick={handlePrintReceipt}
                            className="flex items-center gap-2 px-4 py-2 bg-white border border-indigo-200 text-indigo-700 rounded-lg hover:bg-indigo-50 shadow-sm"
                         >
                            <Printer className="w-4 h-4" /> Print
                         </button>
                         <button 
                            onClick={confirmCashOutForSeller} 
                            disabled={isProcessing}
                            className="flex items-center gap-2 px-4 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700 disabled:opacity-50 shadow-md shadow-slate-200"
                         >
                            <CheckCircle2 className="w-4 h-4" /> 
                            {isProcessing ? 'Processing...' : 'Confirm & Archive'}
                         </button>
                </div>
            </div>
        )}
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 print:hidden">
        
        {/* Quick Actions / Stats */}
        {role !== 'buyer' && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white p-4 rounded-xl shadow-sm border border-indigo-100">
              {role === 'seller' ? (
                  <>
                      <div className="text-indigo-500 text-xs font-semibold uppercase">My Active Items</div>
                      <div className="text-2xl font-bold text-slate-800">{stats.dropped + stats.claimed}</div>
                  </>
              ) : (
                  <>
                      <div className="text-slate-500 text-xs font-semibold uppercase">Total Database</div>
                      <div className="text-2xl font-bold text-slate-800">{items.length}</div>
                  </>
              )}
            </div>
            <div className="bg-white p-4 rounded-xl shadow-sm border border-indigo-100"><div className="text-indigo-500 text-xs font-semibold uppercase">Ready to Pickup</div><div className="text-2xl font-bold text-indigo-600">{stats.dropped}</div></div>
            <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-100"><div className="text-slate-500 text-xs font-semibold uppercase">Claimed (Unpaid)</div><div className="text-2xl font-bold text-slate-600">{stats.claimed}</div></div>
            <div className="bg-white p-4 rounded-xl shadow-sm border border-amber-100"><div className="text-amber-500 text-xs font-semibold uppercase">In Transit</div><div className="text-2xl font-bold text-amber-600">{stats.in_transit}</div></div>
            {/* NEW DROP BUTTON: REMOVED FROM HERE, NOW IN HEADER */}

            {role === 'seller' && (
              <div className="bg-emerald-50 p-4 rounded-xl shadow-sm border border-emerald-200">
                <div className="text-emerald-700 text-xs font-semibold uppercase">Available for Cash Out</div>
                <div className="text-2xl font-bold text-emerald-600">₱{stats.balance.toLocaleString()}</div>
              </div>
            )}
          </div>
        )}

        {/* Filters */}
        <div className="bg-white p-4 rounded-xl shadow-sm border border-indigo-100 mb-6 flex flex-col gap-4">
            <div className="flex flex-col md:flex-row gap-4 justify-between items-center">
                <div className="relative flex-1 w-full flex flex-col sm:flex-row gap-2">
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                        <input type="text" placeholder={role === 'seller' ? "Search my items..." : (role === 'buyer' ? "Type YOUR NAME or TOWN..." : "Search items, buyers, or prices...")} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"/>
                    </div>
                    {/* ADMIN SELLER FILTER DROPDOWN WITH SEARCH */}
                    {role === 'admin' && (
                        <div className="relative sm:w-1/3">
                            <SearchableDropdown 
                                value={sellerFilter}
                                onChange={setSellerFilter}
                                options={sellerList.map(s => ({ value: s.displayName, label: s.displayName }))}
                                defaultOption={{ value: 'all', label: 'All Sellers' }}
                                icon={Filter}
                                placeholder="Search sellers..."
                            />
                        </div>
                    )}
                </div>
                <div className="flex gap-2 w-full md:w-auto overflow-x-auto pb-1 md:pb-0">
                    {role === 'admin' && selectedItems.size > 0 && (
                        <div className="flex gap-2">
                           {/* EXPORT BUTTON (Green) */}
                           <button 
                             onClick={handleExportCSV} 
                             className="px-4 py-2 rounded-lg text-sm font-bold whitespace-nowrap bg-green-100 text-green-700 hover:bg-green-200 flex items-center gap-2 animate-in fade-in slide-in-from-right-5"
                           >
                              <FileDown className="w-4 h-4" /> Export ({selectedItems.size})
                           </button>
                           
                           {/* MASS PULL OUT (Orange) */}
                           <button onClick={handleMassPullOut} className="px-4 py-2 rounded-lg text-sm font-bold whitespace-nowrap bg-orange-100 text-orange-700 hover:bg-orange-200 flex items-center gap-2 animate-in fade-in slide-in-from-right-5"><PackageMinus className="w-4 h-4" /> Pull Out ({selectedItems.size})</button>

                           {/* DELETE BUTTON (Red) */}
                           <button onClick={handleMassDelete} className="px-4 py-2 rounded-lg text-sm font-bold whitespace-nowrap bg-red-100 text-red-600 hover:bg-red-200 flex items-center gap-2 animate-in fade-in slide-in-from-right-5"><Trash2 className="w-4 h-4" /> Delete ({selectedItems.size})</button>
                        </div>
                    )}
                    <button onClick={() => setStatusFilter('all')} className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap ${statusFilter === 'all' ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>All Active</button>
                    <button onClick={() => setStatusFilter('in_transit')} className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap ${statusFilter === 'in_transit' ? 'bg-amber-500 text-white' : 'bg-amber-50 text-amber-700 hover:bg-amber-100'}`}>In Transit</button>
                    <button onClick={() => setStatusFilter('dropped')} className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap ${statusFilter === 'dropped' ? 'bg-indigo-600 text-white' : 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100'}`}>Ready</button>
                    <button onClick={() => setStatusFilter('claimed')} className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap ${statusFilter === 'claimed' ? 'bg-slate-600 text-white' : 'bg-slate-50 text-slate-700 hover:bg-slate-100'}`}>Claimed</button>
                    {(role === 'admin' || role === 'seller') && (
                        <button onClick={() => setStatusFilter('cashed_out')} className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap flex items-center gap-1 ${statusFilter === 'cashed_out' ? 'bg-slate-700 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>{role === 'seller' ? <History className="w-3 h-3" /> : <Archive className="w-3 h-3" />} {role === 'seller' ? 'My History' : 'Archive'}</button>
                    )}
                </div>
            </div>
            
            <div className="flex flex-col xl:flex-row gap-4 justify-between items-center border-t border-slate-100 pt-4">
                
                <div className="flex flex-wrap items-center gap-4 w-full xl:w-auto">
                    {/* PAGINATION SUMMARY */}
                    <div className="text-sm font-semibold text-slate-700 bg-slate-100 px-3 py-1 rounded-full">
                        {sortedItems.length > 0 
                            ? `Showing ${showingStart}-${showingEnd} of ${sortedItems.length} items` 
                            : 'No items found'}
                    </div>

                    {/* DATE FILTERS */}
                    <div className="flex items-center gap-2">
                      <div className="relative">
                        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 text-xs font-bold">From</span>
                        <input 
                          type="date" 
                          value={startDate} 
                          onChange={(e) => setStartDate(e.target.value)} 
                          className="pl-10 pr-2 py-1 bg-slate-50 border border-slate-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                      <div className="relative">
                        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 text-xs font-bold">To</span>
                        <input 
                          type="date" 
                          value={endDate} 
                          onChange={(e) => setEndDate(e.target.value)} 
                          className="pl-8 pr-2 py-1 bg-slate-50 border border-slate-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                      {(startDate || endDate) && (
                        <button onClick={() => { setStartDate(''); setEndDate(''); }} className="text-xs text-red-500 hover:underline">
                          Clear
                        </button>
                      )}
                    </div>
                </div>

                <div className="flex flex-wrap items-center gap-4 w-full xl:w-auto justify-between xl:justify-end">
                  <div className="flex items-center gap-2 text-sm text-slate-500">
                    <ListFilter className="w-4 h-4" />
                    <span className="hidden sm:inline">Sort By:</span>
                    <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="bg-slate-50 border border-slate-200 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-indigo-500">
                        <option value="date">Date Added</option>
                        <option value="name">Item Name</option>
                        <option value="location">Location</option>
                    </select>
                    <button onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')} className="p-1 hover:bg-slate-100 rounded" title={sortOrder === 'asc' ? "Ascending (A-Z)" : "Descending (Z-A)"}>
                        <ArrowUpDown className="w-4 h-4" />
                    </button>
                  </div>

                  {/* DOM FILTER DROPDOWN */}
                  <div className="flex items-center gap-4">
                    <div className="text-sm text-slate-500 flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        <span className="hidden sm:inline">Age:</span>
                        <select value={domFilter} onChange={(e) => setDomFilter(e.target.value)} className="bg-slate-50 border border-slate-200 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-indigo-500">
                          <option value="all">All Time</option>
                          <option value="30">Over 30 Days</option>
                          <option value="60">Over 60 Days</option>
                        </select>
                    </div>

                    <div className="text-sm text-slate-500 flex items-center gap-2">
                        <span>Show:</span>
                        <select value={itemsPerPage} onChange={(e) => setItemsPerPage(Number(e.target.value))} className="bg-slate-50 border border-slate-200 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-indigo-500">
                          <option value="5">5</option>
                          <option value="10">10</option>
                          <option value="20">20</option>
                          <option value="50">50</option>
                        </select>
                    </div>
                  </div>
                </div>
            </div>
        </div>

        {/* LIST VIEW */}
        <div className="bg-white rounded-xl shadow-sm border border-indigo-100 overflow-hidden mb-6">
          <div className="hidden md:grid grid-cols-8 gap-4 p-4 bg-teal-50/50 border-b border-indigo-100 text-xs font-semibold text-slate-800 uppercase tracking-wider items-center">
            <div className="col-span-1">{role === 'admin' && <input type="checkbox" onChange={handleSelectAll} className="w-4 h-4 text-indigo-600 rounded border-gray-300 focus:ring-indigo-500"/>}</div>
            <div className="col-span-1">Date</div><div className="col-span-2">Item</div><div className="col-span-1">Location</div><div className="col-span-1">Buyer</div><div className="col-span-1">Seller</div><div className="col-span-1 text-right">Status</div>
          </div>

          <div className="divide-y divide-indigo-50">
            {paginatedItems.map((item) => (
              <div 
                key={item.id} 
                className={`group hover:bg-teal-50 transition-colors ${role === 'admin' ? 'cursor-pointer' : ''}`}
                onClick={() => role === 'admin' && handleEditClick(item)}
              >
                <div className="hidden md:grid grid-cols-8 gap-4 p-4 items-center">
                  <div className="col-span-1">{role === 'admin' && <input type="checkbox" checked={selectedItems.has(item.id)} onClick={(e) => e.stopPropagation()} onChange={() => handleSelectItem(item.id)} className="w-4 h-4 text-indigo-600 rounded border-gray-300 focus:ring-indigo-500"/>}</div>
                  <div className="col-span-1 text-sm text-slate-500">
                    <div className="font-medium">{formatDate(item.createdAt)}</div>
                    {item.claimedAt && (
                        <div className="text-xs text-slate-600 font-medium mt-1">
                            Claimed: {formatDate(item.claimedAt)}
                        </div>
                    )}
                    <div className="text-xs text-slate-400 mt-1">{item.createdAt.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</div>
                    {role === 'admin' && (item.status === 'dropped' || item.status === 'in_transit') && (
                       <div className={`text-[10px] font-bold mt-1 ${getDOM(item.createdAt) > 30 ? 'text-red-600' : 'text-orange-600'}`}>
                         {getDOM(item.createdAt)} days old
                       </div>
                    )}
                  </div>
                  <div className="col-span-2"><div className="text-sm font-medium text-slate-800">{item.itemName}</div>{role !== 'buyer' && <div><div className={`text-xs ${item.isPaidExternally ? 'text-gray-400 line-through' : 'text-slate-500'}`}>{item.isPaidExternally ? `(${item.price})` : `Price: ${item.price}`}</div>{item.transferFee && item.transferFee !== '0' && <div className="text-xs text-indigo-500">Fee: {item.transferFee}</div>}</div>}
                  {/* DISPLAY REMARKS IF PRESENT */}
                  {(role === 'admin' || role === 'seller') && item.remarks && (
                      <div className="mt-1 flex items-start gap-1 text-xs text-amber-600 bg-amber-50 p-1 rounded border border-amber-100 max-w-fit">
                          <StickyNote className="w-3 h-3 mt-0.5" /> <span className="italic">{item.remarks}</span>
                      </div>
                  )}
                  {/* SHOW WHO EDITED IT (ADMIN ONLY VIEW) */}
                  {role === 'admin' && item.lastEditedBy && (
                      <div className="text-[9px] text-slate-400 mt-1">Last edited by: {item.lastEditedBy}</div>
                  )}
                  </div>
                  <div className="col-span-1 text-sm text-slate-600 flex items-center gap-1"><MapPin className="w-3 h-3 text-slate-400" /> {item.location || '-'}</div>
                  <div className="col-span-1 text-sm text-slate-600">{item.buyerName}</div>
                  <div className="col-span-1 text-sm text-slate-600">{item.sellerName}</div>
                  <div className="col-span-1 text-right flex items-center justify-end gap-2">
                    {role === 'admin' && <button onClick={(e) => { e.stopPropagation(); handleEditClick(item); }} className="p-1 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded" title="Edit Item"><Pencil className="w-4 h-4" /></button>}
                    {item.status === 'in_transit' ? (
                      <>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">In Transit</span>
                        {role === 'admin' && (
                            <button 
                              onClick={(e) => { e.stopPropagation(); handleStatusChange(item.id, 'dropped'); }}
                              className="p-1 ml-2 hover:bg-indigo-100 rounded text-indigo-600"
                              title="Mark as Arrived/Ready"
                            >
                              <Truck className="w-5 h-5" />
                            </button>
                        )}
                      </>
                    ) : item.status === 'dropped' ? (
                      <><span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">Ready</span>
                        {role === 'admin' && (
                            <div className="flex gap-1">
                              <button onClick={(e) => { e.stopPropagation(); handleStatusChange(item.id, 'claimed'); }} className="p-1 hover:bg-slate-100 rounded text-slate-600" title="Admin: Mark Claimed"><CheckCircle2 className="w-5 h-5" /></button>
                              <button onClick={(e) => { e.stopPropagation(); handleStatusChange(item.id, 'pulled_out'); }} className="p-1 hover:bg-orange-100 rounded text-orange-600" title="Admin: Pull Out Item"><PackageMinus className="w-5 h-5" /></button>
                            </div>
                        )}
                      </>) : item.status === 'claimed' ? (
                      <div className="flex flex-col items-end"><span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-800">Claimed</span>{item.claimedAt && <span className="text-[10px] text-slate-400 mt-1 flex items-center gap-1"><CalendarCheck className="w-3 h-3" /> {formatDate(item.claimedAt)}</span>}{role === 'admin' && <button onClick={(e) => { e.stopPropagation(); handleStatusChange(item.id, 'dropped'); }} className="text-[10px] text-slate-400 hover:text-red-500 hover:underline mt-1 flex items-center gap-1" title="Undo / Revert to Ready"><RotateCcw className="w-3 h-3" /> Undo</button>}</div>
                    ) : item.status === 'pulled_out' ? (
                        <div className="flex flex-col items-end">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">Pulled Out</span>
                            {item.pulledOutAt && <span className="text-[10px] text-orange-600 mt-1">{formatDate(item.pulledOutAt)}</span>}
                            {role === 'admin' && <button onClick={(e) => { e.stopPropagation(); handleStatusChange(item.id, 'dropped'); }} className="text-[10px] text-slate-400 hover:text-red-500 hover:underline mt-1 flex items-center justify-end gap-1 w-full"><RotateCcw className="w-3 h-3" /> Undo</button>}
                        </div>
                    ) : item.status === 'disposed' ? (
                        <div className="flex flex-col items-end"><span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-700 text-white"><Ban className="w-3 h-3 mr-1"/> Disposed</span>{role === 'admin' && <button onClick={(e) => { e.stopPropagation(); handleStatusChange(item.id, 'dropped'); }} className="text-[10px] text-slate-400 hover:text-red-500 hover:underline mt-1 flex items-center justify-end gap-1 w-full"><RotateCcw className="w-3 h-3" /> Undo</button>}</div>
                    ) : item.status === 'cashed_out' ? (
                        <div className="flex flex-col items-end">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-800">Cashed Out</span>
                            {item.cashedOutAt && <span className="text-[10px] text-slate-500 mt-1">{formatDate(item.cashedOutAt)}</span>}
                        </div>
                    ) : (<span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-800">Cancelled</span>)}
                  </div>
                </div>
                <div 
                  className={`md:hidden p-4 flex gap-3 ${role === 'admin' ? 'cursor-pointer' : ''}`}
                  onClick={() => role === 'admin' && handleEditClick(item)}
                >
                   {role === 'admin' && <div className="pt-1"><input type="checkbox" checked={selectedItems.has(item.id)} onClick={(e) => e.stopPropagation()} onChange={() => handleSelectItem(item.id)} className="w-5 h-5 text-indigo-600 rounded border-gray-300 focus:ring-indigo-500"/></div>}
                  <div className="flex-1">
                    <div className="flex justify-between items-start mb-2">
                        <div><h4 className="font-semibold text-slate-800">{item.itemName}</h4><p className="text-sm text-slate-500">Buyer: <span className="text-slate-700 font-medium">{item.buyerName}</span></p></div>
                        {item.status === 'in_transit' ? (
                          <div className="text-right">
                             <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">In Transit</span>
                             {role === 'admin' && (
                                <button 
                                  onClick={(e) => { e.stopPropagation(); handleStatusChange(item.id, 'dropped'); }}
                                  className="text-[10px] text-indigo-600 hover:text-indigo-800 hover:underline mt-1 flex items-center justify-end gap-1 w-full"
                                >
                                  <Truck className="w-3 h-3" /> Mark Arrived
                                </button>
                             )}
                          </div>
                        ) : item.status === 'dropped' ? (<span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">Ready</span>) : item.status === 'claimed' ? (<div className="text-right"><span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-800">Claimed</span>{item.claimedAt && <div className="text-[10px] text-slate-400 mt-1">{formatDate(item.claimedAt)}</div>}{role === 'admin' && <button onClick={(e) => { e.stopPropagation(); handleStatusChange(item.id, 'dropped'); }} className="text-[10px] text-slate-400 hover:text-red-500 hover:underline mt-1 flex items-center justify-end gap-1 w-full"><RotateCcw className="w-3 h-3" /> Undo</button>}</div>) : item.status === 'pulled_out' ? (
                            <div className="text-right">
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">Pulled Out</span>
                                {item.pulledOutAt && <div className="text-[10px] text-orange-600 mt-1">{formatDate(item.pulledOutAt)}</div>}
                                {role === 'admin' && <button onClick={(e) => { e.stopPropagation(); handleStatusChange(item.id, 'dropped'); }} className="text-[10px] text-slate-400 hover:text-red-500 hover:underline mt-1 flex items-center justify-end gap-1 w-full"><RotateCcw className="w-3 h-3" /> Undo</button>}
                            </div>
                        ) : item.status === 'disposed' ? (<div className="text-right"><span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-700 text-white"><Ban className="w-3 h-3 mr-1"/> Disposed</span>{role === 'admin' && <button onClick={(e) => { e.stopPropagation(); handleStatusChange(item.id, 'dropped'); }} className="text-[10px] text-slate-400 hover:text-red-500 hover:underline mt-1 flex items-center justify-end gap-1 w-full"><RotateCcw className="w-3 h-3" /> Undo</button>}</div>) : item.status === 'cashed_out' ? (
                            <div className="text-right">
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-800">Done</span>
                                {item.cashedOutAt && <div className="text-[10px] text-slate-500 mt-1">{formatDate(item.cashedOutAt)}</div>}
                            </div>
                        ) : (<span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-800">Cancelled</span>)}
                    </div>
                    <div className="flex flex-col gap-1 mb-2">
                        <div className="text-xs text-slate-500 flex items-center gap-1"><User className="w-3 h-3" /> Seller: {item.sellerName}</div>
                        <div className="text-xs text-slate-500 flex items-center gap-1"><MapPin className="w-3 h-3" /> {item.location || '-'}</div>
                         {/* DISPLAY REMARKS IF PRESENT (MOBILE) */}
                        {(role === 'admin' || role === 'seller') && item.remarks && (
                            <div className="mt-1 flex items-start gap-1 text-xs text-amber-600 bg-amber-50 p-1 rounded border border-amber-100 w-full">
                                <StickyNote className="w-3 h-3 mt-0.5 min-w-[12px]" /> <span className="italic">{item.remarks}</span>
                            </div>
                        )}
                        {/* SHOW WHO EDITED IT (ADMIN MOBILE VIEW) */}
                        {role === 'admin' && item.lastEditedBy && (
                            <div className="text-[9px] text-slate-400 mt-1 flex items-center gap-1"><Pencil className="w-3 h-3" /> By: {item.lastEditedBy}</div>
                        )}
                    </div>
                    <div className="flex justify-between items-end border-t border-indigo-50 pt-2">
                        <div className="text-xs text-slate-400">
                          <div>{formatDate(item.createdAt)}</div>
                          {role === 'admin' && (item.status === 'dropped' || item.status === 'in_transit') && (
                             <div className={`text-[10px] font-bold mt-1 ${getDOM(item.createdAt) > 30 ? 'text-red-600' : 'text-orange-600'}`}>
                               {getDOM(item.createdAt)} days old
                             </div>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                        {role === 'admin' && <button onClick={(e) => { e.stopPropagation(); handleEditClick(item); }} className="p-1 mr-2 text-slate-400"><Pencil className="w-4 h-4" /></button>}
                        {role !== 'buyer' && (<div className="text-right mr-2"><span className={`text-sm font-semibold ${item.isPaidExternally ? 'text-gray-400 line-through' : 'text-slate-700'}`}>{item.isPaidExternally ? `(₱${item.price})` : `₱${item.price}`}</span>{item.transferFee && item.transferFee !== '0' && <div className="text-[10px] text-indigo-500">Fee: {item.transferFee}</div>}</div>)}
                        {item.status === 'dropped' && role === 'admin' && (<div className="flex gap-2"><button onClick={(e) => { e.stopPropagation(); handleStatusChange(item.id, 'claimed'); }} className="bg-slate-600 text-white px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-slate-700">Mark Claimed</button><button onClick={(e) => { e.stopPropagation(); handleStatusChange(item.id, 'pulled_out'); }} className="bg-orange-100 text-orange-600 px-2 py-1.5 rounded-lg text-xs font-medium hover:bg-orange-200"><PackageMinus className="w-4 h-4" /></button></div>)}
                        </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
            {filteredItems.length === 0 && (
              <div className="p-12 text-center text-slate-400">{role === 'buyer' && !searchTerm ? (<><Search className="w-12 h-12 mx-auto mb-3 opacity-20 text-indigo-400" /><p className="font-semibold text-slate-700">Enter your name above</p><p className="text-sm text-indigo-500">Type your name to find your packages.</p></>) : (<><Package className="w-12 h-12 mx-auto mb-3 opacity-20 text-indigo-400" /><p className="text-indigo-500">No items found.</p></>)}</div>
            )}
          </div>
        </div>

        {/* PAGINATION CONTROLS */}
        {sortedItems.length > itemsPerPage && (
            <div className="flex justify-between items-center bg-white p-4 rounded-xl shadow-sm border border-indigo-100">
                <button 
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="p-2 rounded-lg hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed text-slate-700"
                >
                    <ChevronLeft className="w-5 h-5" />
                </button>
                <span className="text-sm text-slate-600 font-medium">Page {currentPage} of {totalPages}</span>
                <button 
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="p-2 rounded-lg hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed text-slate-700"
                >
                    <ChevronRight className="w-5 h-5" />
                </button>
            </div>
        )}
      </main>

      {/* --- NEW: FLOATING CHAT BUTTON (ADMIN & SELLER) --- */}
      {(role === 'admin' || role === 'seller') && (
          <div className="fixed bottom-6 right-6 z-50 print:hidden">
              <button 
                  onClick={() => setIsChatOpen(!isChatOpen)}
                  className="relative bg-slate-600 hover:bg-slate-700 text-white p-4 rounded-full shadow-xl transition-all hover:scale-105"
              >
                  {isChatOpen ? <XCircle className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
                  {unreadTotal > 0 && !isChatOpen && (
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold w-5 h-5 flex items-center justify-center rounded-full border-2 border-white animate-pulse">
                          {unreadTotal}
                      </span>
                  )}
              </button>
          </div>
      )}

      {/* --- NEW: CHAT WINDOW --- */}
      {isChatOpen && (role === 'admin' || role === 'seller') && (
          <div className="fixed bottom-24 right-6 w-96 h-[500px] bg-white rounded-2xl shadow-2xl border border-slate-200 z-50 flex flex-col overflow-hidden animate-in slide-in-from-bottom-10 fade-in duration-200 print:hidden">
              {/* CHAT HEADER */}
              <div className="bg-slate-800 text-white p-4 flex justify-between items-center shrink-0">
                  <div>
                      <h3 className="font-bold flex items-center gap-2">
                          <MessageCircle className="w-5 h-5" /> 
                          {role === 'admin' ? (activeChatId ? 'Chat Details' : 'Messages') : 'Admin Support'}
                      </h3>
                      {role === 'admin' && activeChatId && (
                          <button onClick={() => setActiveChatId(null)} className="text-xs text-slate-200 hover:text-white underline mt-1">
                              &larr; Back to all messages
                          </button>
                      )}
                  </div>
              </div>

              {/* CHAT CONTENT AREA */}
              <div className="flex-1 overflow-y-auto p-4 bg-slate-50">
                  {role === 'admin' && !activeChatId ? (
                      // ADMIN VIEW: CONVERSATION LIST
                      <div className="space-y-2">
                          {chatConversations.length === 0 ? (
                              <div className="text-center text-slate-400 mt-10">No messages yet.</div>
                          ) : (
                              chatConversations.map(chat => (
                                  <div 
                                      key={chat.id} 
                                      onClick={() => setActiveChatId(chat.id)}
                                      className={`p-3 rounded-xl border cursor-pointer transition-colors hover:bg-slate-50 ${chat.adminUnreadCount > 0 ? 'bg-white border-slate-300 shadow-sm' : 'bg-slate-50 border-transparent'}`}
                                  >
                                      <div className="flex justify-between items-start mb-1">
                                          <div className="font-bold text-slate-800">{chat.sellerName}</div>
                                          {chat.lastMessageTime && <div className="text-[10px] text-slate-400">{chat.lastMessageTime.toLocaleDateString([], {month: 'short', day: 'numeric'})} {chat.lastMessageTime.toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'})}</div>}
                                      </div>
                                      <div className="flex justify-between items-center">
                                          <div className="text-xs text-slate-600 truncate max-w-[200px]">{chat.lastMessage}</div>
                                          {chat.adminUnreadCount > 0 && (
                                              <span className="bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">{chat.adminUnreadCount}</span>
                                          )}
                                      </div>
                                  </div>
                              ))
                          )}
                      </div>
                  ) : (
                      // MESSAGE VIEW (Seller or Admin with Active Chat)
                      <div className="space-y-3">
                          {chatMessages.length === 0 && <div className="text-center text-slate-400 mt-10">Start the conversation...</div>}
                          {chatMessages.map(msg => {
                              const isMe = msg.sender === role;
                              return (
                                  <div key={msg.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                                      <div className={`max-w-[80%] rounded-2xl p-3 text-sm ${isMe ? 'bg-slate-600 text-white rounded-br-none' : 'bg-white border border-slate-200 text-slate-800 rounded-bl-none shadow-sm'}`}>
                                          {msg.text}
                                          <div className={`text-[9px] mt-1 text-right ${isMe ? 'text-slate-200' : 'text-slate-400'}`}>
                                              {msg.createdAt.toLocaleDateString([], {month: 'short', day: 'numeric'})} • {msg.createdAt.toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'})}
                                          </div>
                                      </div>
                                  </div>
                              );
                          })}
                          <div ref={messagesEndRef} />
                      </div>
                  )}
              </div>

              {/* CHAT INPUT AREA */}
              {(role === 'seller' || activeChatId) && (
                  <form onSubmit={handleSendMessage} className="p-3 bg-white border-t border-slate-200 shrink-0 flex gap-2">
                      <input 
                          type="text" 
                          placeholder="Type a message..." 
                          value={chatInput}
                          onChange={(e) => setChatInput(e.target.value)}
                          className="flex-1 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-500"
                      />
                      <button 
                          type="submit" 
                          disabled={!chatInput.trim()}
                          className="p-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                          <Send className="w-4 h-4" />
                      </button>
                  </form>
              )}
          </div>
      )}

      {/* --- MODAL: DROP ITEM (ADMIN ONLY NOW) --- */}
      {isFormOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-4">
          <div className="bg-white w-full max-w-lg rounded-2xl p-6 shadow-2xl overflow-y-auto max-h-screen">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-bold text-slate-800">Drop New Item</h3>
              <button onClick={() => { setIsFormOpen(false); setNewItemSeller(''); }} className="text-slate-400 hover:text-indigo-600"><XCircle className="w-6 h-6" /></button>
            </div>
             
            {/* SUCCESS MESSAGE (RAPID ENTRY) */}
            {showSuccessMsg && (
                <div className="mb-4 bg-green-100 border border-green-400 text-green-700 px-4 py-2 rounded flex items-center gap-2 animate-in fade-in slide-in-from-top-2">
                    <Check className="w-4 h-4" /> Item added successfully!
                </div>
            )}

            <form onSubmit={handleAddItem} className="space-y-4">
              
              {/* STATUS SELECTION FOR RAPID ENTRY */}
              <div>
                 <label className="block text-sm font-medium text-slate-700 mb-1">Initial Status</label>
                 <select 
                   value={newItemStatus}
                   onChange={(e) => setNewItemStatus(e.target.value)}
                   className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none bg-white"
                 >
                    <option value="dropped">Ready for Pickup</option>
                    <option value="in_transit">In Transit (On the way)</option>
                 </select>
              </div>

              {/* NEW: SEARCHABLE SELLER SELECTION */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Select Seller</label>
                <SearchableDropdown 
                    value={newItemSeller}
                    onChange={setNewItemSeller}
                    options={sellerList.map(s => ({ value: s.displayName, label: s.displayName }))}
                    defaultOption={{ value: '', label: '-- Choose Seller --' }}
                    placeholder="Search seller..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Item Name</label>
                <input required type="text" placeholder="e.g. White Dress" value={newItemItem} onChange={(e) => setNewItemItem(e.target.value)} className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Buyer Name</label>
                <input required type="text" placeholder="e.g. Maria Cruz" value={newItemBuyer} onChange={(e) => setNewItemBuyer(e.target.value)} className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Price</label>
                    <input type="text" placeholder="e.g. 500" value={newItemPrice} onChange={(e) => setNewItemPrice(e.target.value)} className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Transfer Fee</label>
                    <input type="text" placeholder="e.g. 10" value={newItemTransferFee} onChange={(e) => setNewItemTransferFee(e.target.value)} className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none" />
                  </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Location</label>
                <select 
                  value={newItemLocation}
                  onChange={(e) => setNewItemLocation(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none bg-white"
                >
                  {LU_TOWNS.map(town => (
                    <option key={town} value={town}>{town}</option>
                  ))}
                </select>
              </div>
              
              <div className="flex gap-2 pt-2">
                  <button type="submit" className="flex-1 py-3 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 shadow-md shadow-indigo-200">Confirm Drop</button>
                  <button type="button" onClick={() => { setIsFormOpen(false); setNewItemSeller(''); }} className="px-4 py-3 bg-slate-100 text-slate-600 rounded-lg font-semibold hover:bg-slate-200">Done / Close</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* --- MODAL: EDIT ITEM --- */}
      {isEditModalOpen && editingItem && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-4">
          <div className="bg-white w-full max-w-lg rounded-2xl p-6 shadow-2xl animate-in fade-in zoom-in-95 duration-200">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-bold text-slate-800">Edit Item</h3>
              <button onClick={() => setIsEditModalOpen(false)} className="text-slate-400 hover:text-indigo-600"><XCircle className="w-6 h-6" /></button>
            </div>
            <form onSubmit={handleUpdateItem} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Current Status</label>
                <select
                  value={editingItem.status}
                  onChange={(e) => setEditingItem({ ...editingItem, status: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-500 outline-none bg-white"
                >
                  <option value="in_transit">In Transit</option>
                  <option value="dropped">Ready for Pickup</option>
                  <option value="claimed">Claimed</option>
                  <option value="pulled_out">Pulled Out</option>
                  <option value="disposed">Disposed</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Item Name</label>
                <input 
                  type="text" 
                  value={editingItem.itemName} 
                  onChange={(e) => setEditingItem({...editingItem, itemName: e.target.value})} 
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-500 outline-none" 
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Buyer Name</label>
                <input 
                  type="text" 
                  value={editingItem.buyerName} 
                  onChange={(e) => setEditingItem({...editingItem, buyerName: e.target.value})} 
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-500 outline-none" 
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="flex justify-between items-center mb-1">
                        <label className="block text-sm font-medium text-slate-700">Price</label>
                        {/* UNLOCK BUTTON FOR PRICE */}
                        {!isFinancialsUnlocked && (
                            <button onClick={handleUnlockFinancials} className="text-xs text-slate-600 flex items-center gap-1 hover:underline">
                                <Lock className="w-3 h-3" /> Unlock
                            </button>
                        )}
                        {isFinancialsUnlocked && <span className="text-xs text-green-600 flex items-center gap-1"><Unlock className="w-3 h-3"/> Unlocked</span>}
                    </div>
                    <input 
                      type="text" 
                      value={editingItem.price} 
                      disabled={!isFinancialsUnlocked}
                      onChange={(e) => setEditingItem({...editingItem, price: e.target.value})} 
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-slate-500 outline-none ${!isFinancialsUnlocked ? 'bg-slate-100 text-slate-500 cursor-not-allowed' : 'bg-white border-slate-300'}`} 
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Transfer Fee</label>
                    <input 
                      type="text" 
                      value={editingItem.transferFee || '0'} 
                      disabled={!isFinancialsUnlocked}
                      onChange={(e) => setEditingItem({...editingItem, transferFee: e.target.value})} 
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-slate-500 outline-none ${!isFinancialsUnlocked ? 'bg-slate-100 text-slate-500 cursor-not-allowed' : 'bg-white border-slate-300'}`} 
                    />
                  </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Location</label>
                <select 
                  value={editingItem.location || 'Agoo'}
                  onChange={(e) => setEditingItem({...editingItem, location: e.target.value})}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-500 outline-none bg-white"
                >
                  {LU_TOWNS.map(town => (
                    <option key={town} value={town}>{town}</option>
                  ))}
                </select>
              </div>

              {/* UPDATE 2: REMARKS FIELD */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Admin Remarks (Optional)</label>
                <textarea 
                  value={editingItem.remarks || ''}
                  onChange={(e) => setEditingItem({...editingItem, remarks: e.target.value})}
                  placeholder="e.g. Item damaged, buyer refused..."
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-500 outline-none h-20 text-sm"
                />
              </div>

              {/* NEW: MARK AS PAID EXTERNALLY */}
              <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-xl flex items-center justify-between">
                 <div>
                    <div className="font-semibold text-yellow-800 text-sm">Payment Received Outside?</div>
                    <div className="text-xs text-yellow-600">Toggle this if buyer paid directly.</div>
                 </div>
                 <label className="relative inline-flex items-center cursor-pointer">
                    <input 
                      type="checkbox" 
                      className="sr-only peer"
                      checked={editingItem.isPaidExternally || false}
                      onChange={(e) => setEditingItem({...editingItem, isPaidExternally: e.target.checked})}
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-slate-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-slate-600"></div>
                 </label>
              </div>

              <button type="submit" className="w-full py-3 bg-slate-800 text-white rounded-lg font-semibold hover:bg-slate-900 shadow-md">Update Item</button>
            </form>
          </div>
        </div>
      )}

      {/* --- MODAL: REPORTS (NEW & UPDATED) --- */}
      {isReportsOpen && reports && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center overflow-y-auto">
          <div className="bg-white w-full max-w-4xl rounded-2xl shadow-2xl my-8">
            <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50 rounded-t-2xl">
                <div className="flex items-center gap-2">
                    <BarChart3 className="text-slate-800 w-6 h-6" />
                    <div>
                      <h3 className="text-xl font-bold text-slate-800">Business Insights</h3>
                      <p className="text-sm text-slate-500">Performance & Analytics</p>
                    </div>
                </div>
                <button onClick={() => setIsReportsOpen(false)} className="text-slate-400 hover:text-indigo-600"><XCircle className="w-6 h-6" /></button>
            </div>

            <div className="p-6 bg-slate-50">
                
                {/* UPDATE 4: DAILY SUMMARY */}
                <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-200 mb-6">
                    <div className="flex justify-between items-center mb-4">
                        <div className="text-xs font-bold text-slate-400 uppercase flex items-center gap-2"><CalendarCheck className="w-4 h-4" /> Today's Activity</div>
                        <button onClick={handlePrintLedger} className="flex items-center gap-1 text-xs font-bold bg-slate-800 text-white px-3 py-1.5 rounded hover:bg-slate-700 transition-colors"><FileText className="w-3 h-3" /> Print Ledger</button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                             <div className="text-blue-600 text-sm font-bold">Total Pickups</div>
                             <div className="text-2xl font-bold text-slate-800">{reports.todayPickups}</div>
                        </div>
                        <div className="p-3 bg-green-50 rounded-lg border border-green-100">
                             <div className="text-green-600 text-sm font-bold">Total Collected (In)</div>
                             <div className="text-2xl font-bold text-slate-800">₱{reports.todayIncoming.toLocaleString()}</div>
                        </div>
                        <div className="p-3 bg-orange-50 rounded-lg border border-orange-100">
                             <div className="text-orange-600 text-sm font-bold">Total Payouts (Out)</div>
                             <div className="text-2xl font-bold text-slate-800">₱{reports.todayOutgoing.toLocaleString()}</div>
                        </div>
                    </div>
                </div>

                {/* 1. KEY METRICS ROW */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-100 flex flex-col items-center justify-center text-center">
                        <div className="bg-slate-100 p-2 rounded-full mb-2"><Banknote className="w-6 h-6 text-slate-600" /></div>
                        <div className="text-sm text-slate-500 uppercase font-bold">Total Volume Processed</div>
                        <div className="text-3xl font-bold text-slate-700">₱{reports.totalCashedOutHistory.toLocaleString()}</div>
                        <div className="text-xs text-slate-400 mt-1">Lifetime Cashed Out</div>
                    </div>
                    <div className="bg-white p-4 rounded-xl shadow-sm border border-indigo-100 flex flex-col items-center justify-center text-center">
                        <div className="bg-indigo-100 p-2 rounded-full mb-2"><Package className="w-6 h-6 text-indigo-600" /></div>
                        <div className="text-sm text-slate-500 uppercase font-bold">Active Inventory</div>
                        <div className="text-3xl font-bold text-indigo-600">{stats.dropped + stats.in_transit}</div>
                        <div className="text-xs text-slate-400 mt-1">Ready + In Transit</div>
                    </div>
                    <div className="bg-white p-4 rounded-xl shadow-sm border border-amber-100 flex flex-col items-center justify-center text-center">
                        <div className="bg-amber-100 p-2 rounded-full mb-2"><Calendar className="w-6 h-6 text-amber-600" /></div>
                        <div className="text-sm text-slate-500 uppercase font-bold">Busiest Pickup Day</div>
                        <div className="text-3xl font-bold text-amber-600">{reports.busiestDay.day}</div>
                        <div className="text-xs text-slate-400 mt-1">Based on pickup history</div>
                    </div>
                </div>

                {/* 2. LEADERBOARDS ROW */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    
                    {/* Top Sellers (Volume) */}
                    <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                        <div className="bg-slate-100 p-3 border-b border-slate-200 flex justify-between items-center">
                            <h4 className="font-bold text-slate-700 flex items-center gap-2"><TrendingUp className="w-4 h-4" /> Top Sellers (Items)</h4>
                        </div>
                        <div className="p-0">
                            {reports.topSellersVolume.map(([name, count], index) => (
                                <div key={name} className="flex justify-between items-center p-3 border-b border-slate-50 last:border-0 hover:bg-slate-50">
                                    <div className="flex items-center gap-3">
                                        <span className="w-6 h-6 rounded-full bg-slate-200 text-slate-600 flex items-center justify-center text-xs font-bold">{index + 1}</span>
                                        <span className="font-medium text-slate-700">{name}</span>
                                    </div>
                                    <span className="font-bold text-slate-600">{count} items</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Top Locations */}
                    <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                        <div className="bg-slate-100 p-3 border-b border-slate-200 flex justify-between items-center">
                            <h4 className="font-bold text-slate-700 flex items-center gap-2"><Map className="w-4 h-4" /> Top Locations</h4>
                        </div>
                        <div className="p-0">
                             {reports.topLocations.map(([loc, count], index) => (
                                <div key={loc} className="flex justify-between items-center p-3 border-b border-slate-50 last:border-0 hover:bg-slate-50">
                                    <div className="flex items-center gap-3">
                                        <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-bold">{index + 1}</span>
                                        <span className="font-medium text-slate-700">{loc}</span>
                                    </div>
                                    <span className="font-bold text-slate-600">{count} items</span>
                                </div>
                            ))}
                        </div>
                    </div>

                </div>
            </div>
          </div>
        </div>
      )}

      {/* --- NEW: SELLER ANALYTICS MODAL --- */}
      {isSellerReportsOpen && sellerStats && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center overflow-y-auto">
          <div className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl my-8">
            <div className="p-6 border-b border-indigo-100 flex justify-between items-center bg-indigo-50 rounded-t-2xl">
                <div className="flex items-center gap-2">
                    <BarChart3 className="text-indigo-600 w-6 h-6" />
                    <div>
                      <h3 className="text-xl font-bold text-slate-800">My Business</h3>
                      <p className="text-sm text-slate-500">Performance & Analytics</p>
                    </div>
                </div>
                <button onClick={() => setIsSellerReportsOpen(false)} className="text-slate-400 hover:text-indigo-600"><XCircle className="w-6 h-6" /></button>
            </div>

            <div className="p-6 bg-slate-50">
                {/* TOP METRICS ROW */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-100 flex flex-col items-center justify-center text-center">
                        <div className="bg-slate-100 p-2 rounded-full mb-2"><Banknote className="w-6 h-6 text-slate-600" /></div>
                        <div className="text-xs text-slate-500 uppercase font-bold">Lifetime Sales</div>
                        <div className="text-2xl font-bold text-slate-700">₱{sellerStats.lifetimeSales.toLocaleString()}</div>
                    </div>
                    <div className="bg-white p-4 rounded-xl shadow-sm border border-emerald-100 flex flex-col items-center justify-center text-center">
                        <div className="bg-emerald-100 p-2 rounded-full mb-2"><CheckCircle2 className="w-6 h-6 text-emerald-600" /></div>
                        <div className="text-xs text-slate-500 uppercase font-bold">Pending Payout</div>
                        <div className="text-2xl font-bold text-emerald-600">₱{stats.balance.toLocaleString()}</div>
                    </div>
                    <div className="bg-white p-4 rounded-xl shadow-sm border border-amber-100 flex flex-col items-center justify-center text-center">
                        <div className="bg-amber-100 p-2 rounded-full mb-2"><AlertCircle className="w-6 h-6 text-amber-600" /></div>
                        <div className="text-xs text-slate-500 uppercase font-bold">Stale Inventory</div>
                        <div className="text-2xl font-bold text-amber-600">{sellerStats.staleItems}</div>
                        <div className="text-[10px] text-slate-400">Items {'>'} 30 days</div>
                    </div>
                </div>

                {/* MONTHLY PERFORMANCE */}
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden mb-6">
                    <div className="bg-slate-100 p-3 border-b border-slate-200">
                        <h4 className="font-bold text-slate-700 flex items-center gap-2"><TrendingUp className="w-4 h-4" /> Monthly Performance</h4>
                    </div>
                    <div className="p-4 grid grid-cols-2 gap-4 divide-x divide-slate-100">
                        <div className="text-center">
                            <div className="text-xs text-slate-400 font-bold uppercase mb-1">Last Month</div>
                            <div className="text-xl font-bold text-slate-600">₱{sellerStats.lastMonthSales.toLocaleString()}</div>
                        </div>
                        <div className="text-center">
                            <div className="text-xs text-slate-400 font-bold uppercase mb-1">This Month</div>
                            <div className="text-xl font-bold text-indigo-600">₱{sellerStats.thisMonthSales.toLocaleString()}</div>
                        </div>
                    </div>
                </div>

                {/* RECENT CASHOUTS */}
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                    <div className="bg-slate-100 p-3 border-b border-slate-200">
                        <h4 className="font-bold text-slate-700 flex items-center gap-2"><History className="w-4 h-4" /> Recent Payouts</h4>
                    </div>
                    <div className="p-0">
                        {sellerStats.recentPayouts.length === 0 ? (
                            <div className="text-center p-4 text-slate-400 text-sm">No recent payouts found.</div>
                        ) : (
                            sellerStats.recentPayouts.map((payout, i) => (
                                <div key={i} className="flex justify-between items-center p-3 border-b border-slate-50 last:border-0">
                                    <div className="text-sm font-medium text-slate-700">{payout.dateKey}</div>
                                    <div className="flex flex-col items-end">
                                        <div className="font-bold text-emerald-600">₱{payout.total.toLocaleString()}</div>
                                        <div className="text-[10px] text-slate-400">{payout.count} items</div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </div>
          </div>
        </div>
      )}

      {/* --- MODAL: USER MANAGEMENT (ADMIN ONLY) --- */}
      {isUserMgmtOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center overflow-y-auto">
          <div className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl my-8">
            <div className="p-6 border-b border-indigo-100 flex justify-between items-center">
                <div className="flex items-center gap-2">
                    <Users className="text-slate-800 w-6 h-6" />
                    <div>
                      <h3 className="text-xl font-bold text-slate-800">Manage Users</h3>
                      <p className="text-sm text-slate-500">Reset passwords or remove inactive sellers</p>
                    </div>
                </div>
                <button onClick={() => setIsUserMgmtOpen(false)} className="text-slate-400 hover:text-indigo-600"><XCircle className="w-6 h-6" /></button>
            </div>

            {/* NEW: ADD SELLER FORM INSIDE MODAL */}
            <div className="p-6 bg-indigo-50 border-b border-indigo-100">
               <h4 className="text-sm font-bold text-slate-700 mb-3 flex items-center gap-2">
                  <UserPlus className="w-4 h-4" /> Add New Seller
               </h4>
               <form onSubmit={handleCreateUser} className="flex flex-col sm:flex-row gap-3">
                  <input 
                    type="text" 
                    placeholder="Shop Name" 
                    value={newSellerName}
                    onChange={(e) => setNewSellerName(e.target.value)}
                    className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-500"
                  />
                  <input 
                    type="text" 
                    placeholder="Initial Password" 
                    value={newSellerPass}
                    onChange={(e) => setNewSellerPass(e.target.value)}
                    className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-500"
                  />
                  <button 
                    type="submit"
                    className="bg-slate-800 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-slate-900 transition-colors"
                  >
                    Add Seller
                  </button>
               </form>
            </div>

            <div className="p-6">
               {/* SEARCH BAR FOR USERS */}
               <div className="flex items-center gap-4 mb-4">
                 <div className="relative flex-1">
                   <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                   <input 
                     type="text" 
                     placeholder="Search user..." 
                     value={userMgmtSearch}
                     onChange={(e) => setUserMgmtSearch(e.target.value)}
                     className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-500"
                   />
                 </div>
               </div>

               <div className="text-xs font-semibold text-slate-400 uppercase mb-3">
                  {userMgmtSearch.trim() ? 'Search Results' : 'Type to Search Sellers'}
               </div>
               
               <div className="space-y-2">
                   {filteredSellerList.length === 0 ? (
                       <div className="text-center py-10 text-slate-400">
                          {userMgmtSearch.trim() ? "No matching sellers found." : "Enter a name to manage account."}
                       </div>
                   ) : (
                       filteredSellerList.map((seller) => (
                           <div 
                             key={seller.id} 
                             className="w-full flex justify-between items-center p-4 bg-white hover:bg-indigo-50 rounded-xl border border-indigo-100 transition-colors group"
                           >
                               <div className="flex items-center gap-3">
                                   <div className="bg-indigo-100 p-2 rounded-full text-indigo-600 font-bold w-10 h-10 flex items-center justify-center group-hover:bg-indigo-200">
                                      <User className="w-5 h-5" />
                                   </div>
                                   <div className="text-left">
                                        <div className="font-bold text-slate-800">{seller.displayName}</div>
                                        <div className="text-xs text-slate-500">ID: {seller.id}</div>
                                   </div>
                               </div>
                               
                               <div className="flex items-center gap-2">
                                  <button 
                                    onClick={() => handleResetPassword(seller.id, seller.displayName)}
                                    className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                    title="Reset Password"
                                  >
                                    <KeyRound className="w-5 h-5" />
                                  </button>
                                  <button 
                                    onClick={() => handleDeleteUser(seller.id, seller.displayName)}
                                    className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                    title="Remove Access"
                                  >
                                    <Trash2 className="w-5 h-5" />
                                  </button>
                               </div>
                           </div>
                       ))
                   )}
               </div>
            </div>
          </div>
        </div>
      )}

      {/* --- MODAL: ADMIN CASH OUT --- */}
      {isCashOutModalOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center overflow-y-auto">
          <div className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl my-8">
            <div className="p-6 border-b border-indigo-100 flex justify-between items-center print:hidden">
                <div>
                    <h3 className="text-xl font-bold text-slate-800">Cash Out</h3>
                    <p className="text-sm text-slate-500">{selectedSellerForCashout ? `Invoice for ${selectedSellerForCashout.name}` : 'Select a seller to pay out'}</p>
                </div>
                <div className="flex items-center gap-4">
                    <button onClick={() => { setIsCashOutModalOpen(false); setSelectedSellerForCashout(null); }} className="text-slate-400 hover:text-indigo-600"><XCircle className="w-6 h-6" /></button>
                </div>
            </div>

            {!selectedSellerForCashout && (
                <div className="p-6">
                   <div className="flex items-center gap-4 mb-4">
                     <div className="relative flex-1">
                       <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                       <input 
                         type="text" 
                         placeholder="Search seller..." 
                         value={cashOutSearchTerm}
                         onChange={(e) => setCashOutSearchTerm(e.target.value)}
                         className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-500"
                       />
                     </div>
                   </div>
                   
                   <div className="space-y-2">
                       {paginatedCashOutSellers.length === 0 ? (
                           <div className="text-center py-10 text-slate-400">No pending balances found.</div>
                       ) : (
                           paginatedCashOutSellers.map((seller, idx) => (
                               <button 
                                 key={idx} 
                                 onClick={() => setSelectedSellerForCashout(seller)}
                                 className="w-full flex justify-between items-center p-4 bg-white hover:bg-indigo-50 rounded-xl border border-indigo-100 transition-colors group"
                               >
                                   <div className="flex items-center gap-3">
                                       <div className="bg-indigo-100 p-2 rounded-full text-indigo-600 font-bold w-10 h-10 flex items-center justify-center group-hover:bg-indigo-200">₱</div>
                                       <div className="text-left">
                                            <div className="font-bold text-slate-800">{seller.name}</div>
                                            <div className="text-xs text-slate-500">{seller.items.length} items to claim</div>
                                       </div>
                                   </div>
                                   <div className="font-bold text-lg text-slate-600">₱{seller.total}</div>
                               </button>
                           ))
                       )}
                   </div>

                   {/* Cash Out Pagination */}
                   {filteredCashOutSellers.length > CASH_OUT_ITEMS_PER_PAGE && (
                      <div className="flex justify-between items-center mt-4 pt-4 border-t border-slate-100">
                        <button 
                          onClick={() => setCashOutPage(p => Math.max(1, p - 1))}
                          disabled={cashOutPage === 1}
                          className="p-2 rounded-lg hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed text-slate-700"
                        >
                          <ChevronLeft className="w-4 h-4" />
                        </button>
                        <span className="text-xs text-slate-500 font-medium">Page {cashOutPage} of {totalCashOutPages}</span>
                        <button 
                          onClick={() => setCashOutPage(p => Math.min(totalCashOutPages, p + 1))}
                          disabled={cashOutPage === totalCashOutPages}
                          className="p-2 rounded-lg hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed text-slate-700"
                        >
                          <ChevronRight className="w-4 h-4" />
                        </button>
                      </div>
                   )}
                </div>
            )}

            {selectedSellerForCashout && (
                <div className="p-8" id="printable-receipt">
                    <div className="text-center mb-8">
                        <h1 className="text-2xl font-bold text-slate-800">{APP_NAME}</h1>
                        <p className="text-sm text-indigo-600">Cash Out Receipt / Invoice</p>
                        <p className="text-xs text-slate-500 mt-1">Date: {new Date().toLocaleDateString()}</p>
                        <p className="text-[10px] text-slate-400 mt-1">Processed by: {userName}</p>
                    </div>

                    <div className="bg-teal-50 p-4 rounded-lg border border-indigo-100 mb-6">
                        <div className="flex justify-between mb-2">
                            <span className="text-slate-500">Seller Name:</span>
                            <span className="font-bold text-lg text-slate-800">{selectedSellerForCashout.name}</span>
                        </div>
                        <div className="flex justify-between">
                             <span className="text-slate-500">Transaction ID:</span>
                             <span className="font-mono text-xs text-slate-600">{selectedSellerForCashout.items[0]?.id.substring(0,8).toUpperCase()}...</span>
                        </div>
                    </div>

                    <table className="w-full mb-6 text-sm">
                        <thead>
                            <tr className="border-b-2 border-indigo-100 text-left">
                                <th className="py-2 text-slate-500">Date Claimed</th>
                                <th className="py-2 text-slate-500">Item</th>
                                <th className="py-2 text-slate-500">Location</th>
                                <th className="py-2 text-slate-500">Buyer</th>
                                <th className="py-2 text-slate-500 text-right">Fee</th>
                                <th className="py-2 text-slate-500 text-right">Price</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-indigo-50">
                            {selectedSellerForCashout.items.map(item => (
                                <tr key={item.id} className="break-inside-avoid">
                                    <td className="py-2 text-slate-500 text-xs">{formatDate(item.claimedAt || item.createdAt)}</td>
                                    <td className="py-2 text-slate-700">{item.itemName}</td>
                                    <td className="py-2 text-slate-500 text-xs">{item.location}</td>
                                    <td className="py-2 text-slate-500">{item.buyerName}</td>
                                    <td className="py-2 text-right text-indigo-500 text-xs">{item.transferFee}</td>
                                    <td className="py-2 text-right font-medium text-slate-700">
                                        {item.isPaidExternally ? <span className="text-gray-400 line-through decoration-double">({item.price})</span> : `₱${item.price}`}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr className="border-t-2 border-slate-300">
                                <td colSpan="5" className="py-2 text-slate-500 text-right pr-4">Total Item Prices:</td>
                                <td className="py-2 text-slate-700 text-right font-medium">₱{selectedSellerForCashout.itemsTotal.toLocaleString()}</td>
                            </tr>
                            <tr>
                                <td colSpan="5" className="py-2 text-slate-500 text-right pr-4">Total Fees:</td>
                                <td className="py-2 text-slate-700 text-right font-medium">₱{selectedSellerForCashout.feesTotal.toLocaleString()}</td>
                            </tr>
                            <tr className="border-t border-slate-800 break-inside-avoid bg-slate-50 print:bg-transparent">
                                <td colSpan="5" className="py-4 font-bold text-slate-800 text-right pr-4">GRAND TOTAL PAYOUT:</td>
                                <td className="py-4 font-bold text-xl text-slate-800 text-right">₱{selectedSellerForCashout.total.toLocaleString()}</td>
                            </tr>
                        </tfoot>
                    </table>

                    <div className="mt-12 pt-8 border-t border-slate-300 hidden print:block">
                        <div className="flex justify-between text-xs text-slate-500">
                            <div className="text-center w-1/3"><div className="h-8 border-b border-slate-300 mb-1"></div>Seller Signature</div>
                            <div className="text-center w-1/3"><div className="h-8 border-b border-slate-300 mb-1"></div>Admin Signature: {userName}</div>
                        </div>
                    </div>

                    <div className="flex gap-3 justify-end mt-6 border-t border-indigo-100 pt-6 print:hidden">
                         <button onClick={() => setSelectedSellerForCashout(null)} className="px-4 py-2 text-slate-500 hover:text-indigo-600">Back</button>
                         <button 
                            onClick={handlePrintReceipt}
                            className="flex items-center gap-2 px-4 py-2 bg-white border border-indigo-200 text-indigo-700 rounded-lg hover:bg-indigo-50 shadow-sm"
                         >
                            <Printer className="w-4 h-4" /> Print
                         </button>
                         <button 
                            onClick={confirmCashOutForSeller} 
                            disabled={isProcessing}
                            className="flex items-center gap-2 px-4 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700 disabled:opacity-50 shadow-md shadow-slate-200"
                         >
                            <CheckCircle2 className="w-4 h-4" /> 
                            {isProcessing ? 'Processing...' : 'Confirm & Archive'}
                         </button>
                    </div>
                </div>
            )}
          </div>
        </div>
      )}

    </div>
  );
}
